/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Oracle dialect-native resolver for the `oracle` engine (PRD Slice
 * "Oracle Resolver — Core").
 *
 * Pipeline mirrors `mssqlResolver.ts` (pure-text after GRA-7 Slice A4 — no
 * tree-sitter parse in the resolver hot path; chosen template because
 * Oracle has a schema-qualified namespace, like MSSQL's database.schema,
 * mapped onto the bus's `database` slot per the existing oracle adapter):
 *   1. Derive `CompletionContext` from source text (statement isolation +
 *      paren-frame state machine + dotted-base extractor + alias/CTE
 *      bindings).
 *   2. Bail when inside a string or comment.
 *   3. Build `CompletionCandidate[]` from:
 *        - alias-dot column expansion (when the dotted base resolves to a
 *          bound alias),
 *        - schema-qualified table list (`SCHEMA.<here>`),
 *        - position-aware columns + tables + routines + DUAL + keywords +
 *          built-in functions,
 *        - synonym chain: local table/view → user-private synonym → public
 *          synonym (resolved through the cached snapshot).
 *   4. Rank via `rankCandidates`.
 *   5. Translate each `MonacoCompletionItem` to Monaco's runtime shape.
 *
 * Oracle specifics:
 *   - **Identifier folding** is to UPPERCASE for unquoted identifiers
 *     (`employees` → `EMPLOYEES`); `"Quoted"` preserves case verbatim.
 *     Output canonicalises to the underlying object's stored form (which is
 *     UPPER for unquoted DDL — every `ALL_OBJECTS.OBJECT_NAME` is upper).
 *   - **DUAL** is always visible at FROM position even though it's not in
 *     the snapshot — emitted as a built-in pseudo-table so users can write
 *     `SELECT … FROM DUAL`.
 *   - **`:bind`** (Oracle bind variables) and **`&sub` / `&&sub`** (SQL*Plus
 *     substitution variables) are recognized and the resolver returns no
 *     suggestions. The `:` prefix opens an empty-result branch immediately;
 *     the `&` prefix is detected via source-text scan because tree-sitter
 *     parks them under ERROR nodes.
 *   - **Synonym chain**: when an unqualified bare relation doesn't match a
 *     local table/view, the resolver falls through to the cached synonym
 *     list (private first, public second). The synonym names themselves
 *     surface as table-tier candidates so users can type / fuzzy-match
 *     them; alias-dot resolution against a synonym hops to the target
 *     table's columns transparently.
 *
 * Pure module — no Monaco/React imports at module-level. The `monaco`
 * runtime instance flows through resolver input.
 */

import type {
  AliasBinding,
  ColumnMeta,
  CompletionCandidate,
  CompletionPositionKind,
  CteBinding,
  DatabaseEngineId,
  FkEdge,
  TableMeta,
} from '@/core/engines';
import {
  ensureOracleSchema,
  getCachedOracleSchema,
} from '@/features/connections/services/language-services/oracleSchemaCache';
import { ORACLE_DATA_DICTIONARY_VIEWS } from '@/features/connections/services/language-services/oracleLanguageMetadata';
import type {
  OracleSchemaPackage,
  OracleSchemaSequence,
  OracleSchemaSynonym,
} from '@/features/connections/services/language-services/oracleSchemaTypes';
import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { deriveCompletionContext } from '@/features/intellisense/context/deriveContext';
import { applyGroupByScopeFilter } from '@/features/intellisense/context/groupByScope';
import { isAfterCompletedFromRelation } from '@/features/intellisense/context/isAfterCompletedFromRelation';
import { isAfterCompletedSelectProjection } from '@/features/intellisense/context/isAfterCompletedSelectProjection';
import { scanOptionsFor } from '@/features/intellisense/context/slotTextScan';
import { applyParenScopeFilter } from './parenScopeFilter';
import { applyIndexTargetFilter } from './indexTargetFilter';
import { emitIndexNameCandidates } from './indexNameSlot';
import { applyPlsqlParenFilter } from './plsqlParenFilter';
import { isPivotForColumnPosition } from '@/features/intellisense/context/pivotSlot';
import { filterAlreadyAssignedSetColumns } from './filterAssignedSetColumns';
import { emitAggregateArgHints, emitAggregateTailHints } from './aggregateArgHints';
import { emitProjectionWildcard } from './projectionWildcard';
import { emitSavepointNameCandidates } from '../context/savepointSlot';
import { isAtRenameTargetSlot } from '../context/renameTargetSlot';
import { emitTriggerPseudoRelationNames } from '../resolver/pseudoTables';
import { isAfterCompletedListItem } from '@/features/intellisense/context/nextKeywordSlot';
import {
  classifyNextKeywordSlot,
  type NextKeywordSlotResult,
} from '@/features/intellisense/context/nextKeywordSlot';
import { getLegalNextKeywords, isRowLimitSlot } from '@/features/intellisense/context/legalNextKeywords';
import {
  emitCompoundKeywordRemainders,
  keywordLabelsAlreadyOffered,
} from './compoundKeywordRemainder';
import { buildRowLimitOperandCandidates } from './rowLimitOperand';
import {
  applyPivotTransform,
  parsePivotClause,
} from '@/features/intellisense/pivot/parsePivotClause';
import { PIVOT_SYNTHETIC_ALIAS } from '@/features/intellisense/resolver/aliasScope';
import {
  inferSubqueryColumns,
  inferSubqueryProjection,
} from '@/features/intellisense/resolver/inferSubqueryColumns';
import { resolveCteColumns } from '@/features/intellisense/resolver/resolveCteColumns';
import { emitSelectListAliasCandidates } from '@/features/intellisense/resolver/selectListAliases';
import { emitValueSlotCandidates } from '@/features/intellisense/valueSlot/emitValueSlotCandidates';
import { emitPredicateHeadCandidates } from '@/features/intellisense/predicateHead/emitPredicateHeadCandidates';
import { buildOracleValueSlotAdapter, collectOracleScalarRoutineDescriptors } from './oracleValueSlotAdapter';
import {
  emitResolvedValueCandidates,
  legalValueKeywordsFor,
  type ResolvedValueTarget,
} from '../valueSlot/emitResolvedValueCandidates';
import { coarseTypeOfDataType } from '../valueSlot/types';
import { findInsertValuesTarget } from '../context/findInsertValuesTarget';
import { findInsertReturningTarget } from '../context/findInsertReturningTarget';
import { computeClauseContext } from '@/features/sql-grammar/document/editorContextClause';
import {
  generateFkJoinSnippet,
  makeApplicableFksFinder,
} from '@/features/intellisense/fkJoin/fkJoinSnippet';
import { makeFkEndpointMatcher } from '@/features/intellisense/fkJoin/fkEndpointMatch';
import {
  computeBufferReferencedTableKeys,
  computeFkLinkedTableKeys,
  fkLinkedTableKey,
  unionTableKeys,
} from '@/features/intellisense/fkJoin/fkLinkedTableKeys';
import { promoteCaretBindingRelation } from '@/features/intellisense/fkJoin/caretBindingPromotion';
import { rankCandidates, buildRankOptions } from '@/features/intellisense/ranking/rank';
import { emitAliasCandidate } from './aliasSuggestions';
import { enrichCandidatesWithDocs } from './completionDocs';
import { toMonacoItem } from './monacoItem';

import type {
  OrchestratorResolver,
  OrchestratorResolverInput,
  OrchestratorResolverResult,
} from '../orchestrator/types';

import { emitCreateObjectNamespaces } from './createObjectNamespaces';
import {
  emitMergeValuesQualifiedColumns,
  isAtMergeValuesElement,
  partitionMergeBindings,
} from '@/features/intellisense/resolver/mergeValuesQualified';
import {
  doubleQuote,
  eqIdentifier,
  foldIdentifier,
  formatIdentifier,
  formatQualifiedTwoPart,
  needsDoubleQuote,
  unquoteIdentifier,
} from './oracleIdentifiers';
import {
  ORACLE_KEYWORD_SET,
  getOracleKeywordsForPosition,
} from './oracleKeywords';
import {
  getOracleCursorTypes,
  getOracleDataTypes,
  getOraclePlsqlTypePatterns,
  isOracleDataTypePosition,
  isOraclePlsqlDataTypePosition,
  isOracleRoutineReturnTypeSlot,
} from './oracleDataTypes';
import { findTriggerOnTableAhead, isInsideTableDdlStatement } from './ddlStatementScan';
import { getOracleBuiltinFunctions } from './oracleBuiltinFunctions';
import {
  getOracleHierarchicalCandidates,
  isInConnectByContext,
} from './oracleHierarchical';
import {
  ORACLE_HINT_NAMES,
  ORACLE_SEQUENCE_METHODS,
  findEnclosingOracleHint,
} from './oracleExtras';
import {
  buildOracleBuiltinPackageMemberCandidates,
  buildOraclePackageMemberCandidates,
  isOraclePackageStatementPosition,
} from './oraclePackageMemberCandidates';
import { emitTypeAwareOperators, emitTypeAwareExpressionOperators } from '../operators/emitJsonOperatorCandidates';
import { findPrecedingExpressionType } from '../operators/findPrecedingExpressionType';
import { makeRoutineReturnResolver } from '../operators/routineReturnType';
import {
  emitJsonPathCandidates,
  findJsonPathStringPosition,
} from '../operators/emitJsonPathCandidates';
import { findPrecedingColumnOperand } from '../operators/findPrecedingColumnOperand';
import { emitEnumValueCandidates } from '../operators/emitEnumValueCandidates';
import { computeEnumValueEdit } from '../operators/enumValueInsertion';
import { extractJsonPathFromCursorOracle } from '../operators/extractJsonPathFromCursor.oracle';
import { findValueStringBodyStart } from '../context/comparisonScanHelpers';
import { detectComparisonValueContext } from '../context/detectComparisonValueContext';
import { findTypedInsertColumns } from '../context/findTypedInsertColumns';
import { isAtInsertTargetSlot } from '@/features/intellisense/context/isAtInsertTargetSlot';
import { boundTablesFirst, resolveColumnRefToTable, type RelationResolver } from '../context/resolveColumnRefToTable';
import {
  applyInBufferTableOps,
  findInBufferCreatedTables,
  scanInBufferTableOps,
  type InBufferRename,
  type InBufferTable,
} from '../context/inBufferTables';
import { tokenize, type Token } from '../context/tokenizer';
import { getPathHints } from '@/features/sql-resolver/json/pathHints';
import {
  getJsonPathHints,
  isJsonColumnDeclared,
  getLooseRelationships,
} from '@/features/sql-resolver/metaInfoHints/metaInfoHintProvider';
import { emitLooseRelJoinSnippets } from '@/features/intellisense/fkJoin/looseFkJoinSnippet';
import { emitNamedWindowCandidates } from '@/features/intellisense/resolver/inferNamedWindows';
import { applyJoinOnColumnRanking } from '@/features/intellisense/fkJoin/fkColumnRanking';
import {
  applyReferencesTargetPromotion,
  findFkSourceColumnStems,
} from '@/features/intellisense/fkJoin/referencesTargetInference';
import {
  applyJsonFunctionArgPromotion,
  collectBufferJsonFunctionColumns,
  isAtJsonFunctionFirstArg,
} from '@/features/intellisense/fkJoin/jsonFunctionArgAffinity';
import {
  isInsideOracleProceduralBody,
  emitOracleProceduralLocalCandidates,
} from './oracleProceduralLocals';
import { isAtProceduralStatementStart } from '@/features/intellisense/context/proceduralStatementStart';
import { findOracleProceduralRowtypeBindings } from './oracleRowtypeBindings';
import { findOracleProceduralForInBindings } from './oracleForInBindings';
import { findOracleTriggerHostTable, isColonPrefixedAtCursor } from './oracleTriggerHostTable';
import { findOracleEnclosingObjectTypeName } from './oracleSelfScope';
import {
  ORACLE_PLSQL_EXCEPTION_NAMES,
  isAtPlsqlExceptionWhenSlot,
} from './oraclePlsqlExceptions';
import {
  classifyGraphSlot,
  collectGraphElementVariables,
} from '@/features/sql-grammar/document/editorContextSlot.graph';
import { tokenizeUntil } from '@/features/sql-grammar/document/editorContextClause';
import {
  PGQ_FUNCTIONS,
  findGraphTableInfo,
  graphPatternContext,
  graphIdentEquals,
  graphStructuralKeyword,
  graphStructuralKeywordCandidate,
  type GraphTableInfo,
} from './pgqVocabulary';
import type { PropertyGraphMeta } from '@/core/engines/intellisenseTypes';
import type { LooseRelationship } from '@/types/electron';

const ENGINE: DatabaseEngineId = 'oracle';

/**
 * F5.3 Wave-4 — `DROP TABLE <name> |` trailing-keyword slot. Oracle accepts
 * `CASCADE CONSTRAINTS` and `PURGE` (independently and in either order)
 * after the table name in a DROP TABLE statement. The grammar classifies
 * this slot as `'from'` so the resolver was emitting tables; this regex
 * fires only when the table-name token sits between `DROP TABLE` and the
 * cursor (with optional partial-keyword prefix typed).
 */
const ORACLE_DROP_TABLE_TRAILING_RE =
  /\bDROP\s+TABLE\s+(?:"[^"]+"|[A-Za-z_][\w$]*)(?:\.(?:"[^"]+"|[A-Za-z_][\w$]*))?(?:\s+[A-Za-z_][\w$]*)*\s+[A-Za-z_]?[\w$]*\s*$/i;

const ORACLE_DROP_TABLE_TRAILING_KEYWORDS: readonly string[] = [
  'CASCADE CONSTRAINTS',
  'PURGE',
];

/**
 * Position kinds where Oracle PL/SQL procedural locals / parameters are NOT
 * valid completion candidates — FROM table list, DDL identifier slots, and
 * dotted-access (which needs the dotted-base's members, not the routine's
 * locals). At expression positions, procedural locals remain visible.
 */
function isOracleNonExpressionSlot(
  positionKind: CompletionPositionKind | undefined,
): boolean {
  if (!positionKind) return false;
  return (
    positionKind === 'from' ||
    positionKind === 'dotted-access' ||
    positionKind.startsWith('ddl-')
  );
}

function isOracleDropTableTrailingPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return ORACLE_DROP_TABLE_TRAILING_RE.test(slice);
}

/**
 * F5.3 Wave-4 — Oracle FK referential-action slot `ON {DELETE|UPDATE} |`.
 * Oracle accepts `CASCADE`, `SET NULL`, and `NO ACTION` here (`RESTRICT` is
 * not Oracle syntax — explicitly excluded per the script.30 §5 spec). Fires
 * regardless of positionKind; the text-walk regex is the gate.
 */
const ORACLE_FK_ACTION_RE =
  /\bON\s+(?:DELETE|UPDATE)\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const ORACLE_FK_ACTION_KEYWORDS: readonly string[] = [
  'CASCADE',
  'SET NULL',
  'NO ACTION',
];

function isOracleFkActionPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return ORACLE_FK_ACTION_RE.test(slice);
}

/**
 * F5.3 Wave-4 — Oracle MATERIALIZED VIEW `REFRESH |` slot. Oracle accepts
 * `ON DEMAND`, `ON COMMIT` (refresh triggers) and `FAST`, `COMPLETE`,
 * `FORCE`, `NEVER` (refresh methods) after the REFRESH keyword in a
 * CREATE MATERIALIZED VIEW statement. Gated by both the regex (REFRESH
 * preceding the cursor) and a CREATE MATERIALIZED VIEW prefix check.
 */
const ORACLE_MV_REFRESH_RE =
  /\bREFRESH\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const ORACLE_MV_REFRESH_KEYWORDS: readonly string[] = [
  'ON DEMAND',
  'ON COMMIT',
  'FAST',
  'COMPLETE',
  'FORCE',
  'NEVER',
  // Data-dependency anchor (which row identity to use for fast refresh):
  'WITH PRIMARY KEY',
  'WITH ROWID',
  // Refresh-schedule clauses (deferred-refresh timing):
  'START WITH',
  'NEXT',
];

function isOracleMvRefreshPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  if (!ORACLE_MV_REFRESH_RE.test(slice)) return false;
  // Confirm the enclosing statement is CREATE [OR REPLACE] MATERIALIZED VIEW.
  return /\bCREATE\s+(?:OR\s+REPLACE\s+)?MATERIALIZED\s+VIEW\b/i.test(slice);
}

/**
 * F5.3 Wave-4 — Oracle CREATE VIEW `WITH |` clause options. Oracle accepts
 * only two view-clause options after `WITH`: `READ ONLY` and `CHECK OPTION`.
 * Gated by both the regex (WITH preceding the cursor at a view-options slot)
 * and a CREATE [OR REPLACE] VIEW prefix check so we don't fire inside CTE
 * `WITH` clauses or other unrelated WITH usages.
 */
const ORACLE_VIEW_WITH_OPTION_RE =
  /\bWITH\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const ORACLE_VIEW_WITH_OPTION_KEYWORDS: readonly string[] = [
  'READ ONLY',
  'CHECK OPTION',
];

function isOracleViewWithOptionPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  if (!ORACLE_VIEW_WITH_OPTION_RE.test(slice)) return false;
  // Confirm the enclosing statement is CREATE [OR REPLACE] VIEW (not a CTE
  // `WITH name AS (...)`, not a CREATE MATERIALIZED VIEW LOG WITH clause).
  if (!/\bCREATE\s+(?:OR\s+REPLACE\s+)?VIEW\b/i.test(slice)) return false;
  if (/\bMATERIALIZED\b/i.test(slice)) return false;
  return true;
}

/**
 * F5.3 Wave-4 — Oracle window-frame slot detectors. Same pattern as the
 * pgsql window-frame detectors. The grammar classifier returns `'unknown'`
 * inside the frame clause; the text-walk regex is the gate.
 */
const ORACLE_FRAME_START_RE =
  /\b(?:ROWS|RANGE)\s+BETWEEN\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const ORACLE_FRAME_START_KEYWORDS: readonly string[] = [
  'UNBOUNDED PRECEDING',
  'CURRENT ROW',
];

const ORACLE_FRAME_END_RE =
  /\b(?:ROWS|RANGE)\s+BETWEEN\s+(?:UNBOUNDED\s+(?:PRECEDING|FOLLOWING)|\d+\s+(?:PRECEDING|FOLLOWING)|CURRENT\s+ROW)\s+AND\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const ORACLE_FRAME_END_KEYWORDS: readonly string[] = [
  'UNBOUNDED FOLLOWING',
  'CURRENT ROW',
];

function isOracleFrameStartPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return ORACLE_FRAME_START_RE.test(slice);
}

function isOracleFrameEndPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return ORACLE_FRAME_END_RE.test(slice);
}

export const oracleResolver: OrchestratorResolver = {
  engineId: ENGINE,
  resolve: async (input) => resolveOracle(input),
};

async function resolveOracle(
  input: OrchestratorResolverInput,
): Promise<OrchestratorResolverResult> {
  const { model, position, monaco } = input;
  const text = model.getValue();
  const offset = model.getOffsetAt(position);

  // SQL*Plus substitution variables (&sub / &&sub) and bind variables (:bind)
  // are NOT identifiers — never offer completion at those positions. Cheap
  // source-text check before parsing because tree-sitter parks them under
  // ERROR nodes anyway.
  if (isCursorAfterBindOrSubstitution(text, offset)) {
    return { suggestions: [] };
  }

  // Oracle hint comment `/*+ … */` short-circuit. The tree-sitter Oracle
  // grammar parks the body under `marginalia` so the standard string/comment
  // mask returns true; we override that bail-out here and emit hint
  // candidates instead. Source-text scan is cheap and runs before parsing
  // so the hot path stays fast.
  const hintHit = findEnclosingOracleHint(text, offset);
  if (hintHit) {
    const wordInfo = model.getWordUntilPosition(position);
    const range = {
      startLineNumber: position.lineNumber,
      startColumn: wordInfo.startColumn,
      endLineNumber: position.lineNumber,
      endColumn: wordInfo.endColumn,
    };
    return { suggestions: buildHintSuggestions(monaco, range) };
  }

  // Script-mode `ALTER SESSION SET CURRENT_SCHEMA = <schema>` overrides the
  // connection-default schema for snapshot lookup so completion in
  // `ALTER SESSION SET CURRENT_SCHEMA = APP_SCOPE; SELECT * FROM <cursor>` sees
  // APP_SCOPE's tables instead of the tab's connection-default schema.
  const scriptScope = input.scriptScope ?? {};
  const ctx = deriveCompletionContext(text, offset, ENGINE, scriptScope);
  const rankOptions = buildRankOptions(text, ctx, offset, input);
  const effectiveSchema = scriptScope.currentSchema ?? input.database;
  // F5.3 Wave-4 → plan19 T7 — the fine-grained DDL slot kinds
  // (`ddl-index-column`, `ddl-references-column-list`, `ddl-fk-target`,
  // `ddl-create-trigger-timing`, …) now arrive on `ctx.positionKind` from the
  // token slot seam (`classifyDdlSlot` via `deriveCompletionContext`'s
  // DDL-override gate). The string-walk `classifyDdlPositionKind` override
  // that used to run here is retired.

  const snap = input.connectionId
    ? getSchemaBusSnapshot(input.connectionId, effectiveSchema)
    : null;

  // In-buffer DDL — surface tables CREATEd / RENAMEd earlier in the buffer and
  // retire DROPped ones so completion reflects the editor state at the caret
  // (parity with the other four engines — 1c of the in-buffer-DDL group). Apply
  // RENAME/DROP to the snapshot FIRST, then merge in-buffer CREATEs on top — so a
  // `DROP TABLE foo; CREATE TABLE foo;` re-create leaves `foo` present (applying
  // ops after the merge would drop the just-created table).
  const augmentedTables = mergeInBufferTables(
    applyInBufferTableOps(snap?.tables ?? [], scanInBufferTableOps(text, offset, ENGINE), {
      keyOf: (t) => `${(t.schema ?? '').toLowerCase()}.${t.name.toLowerCase()}`,
      keyOfRef: (q, n) => `${(q ?? '').toLowerCase()}.${n.toLowerCase()}`,
      renameTable: (existing, op: InBufferRename) => ({
        name: op.toName,
        ...(op.toQualifier !== null ? { schema: op.toQualifier } : {}),
        kind: existing?.kind ?? 'table',
        columns: existing?.columns ?? [],
      }),
    }),
    findInBufferCreatedTables(text, offset, ENGINE),
  );
  // The table a relation reference names, as Oracle resolves it (the snapshot
  // holds the connected schema's objects) — the MetaInfo enum / JSON-path
  // collectors read the hints of THAT table.
  const resolveTable = oracleTableResolver(augmentedTables);

  // JCS Slice C4 — JSON path completion fires even inside a single-quoted
  // string when the string is in a recognised JSON-path position (after
  // `JSON_VALUE(col, `, `JSON_QUERY(col, `, `JSON_EXISTS(col, `). Runs BEFORE
  // the string-bail so the cursor inside `JSON_VALUE(doc, '|` still gets
  // candidates.
  if (input.connectionId && effectiveSchema) {
    const pathHintsCandidates = collectOracleJsonPathCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: effectiveSchema,
      snapshotTables: augmentedTables,
      aliasBindings: ctx.aliasBindings,
      resolveTable,
    });
    if (pathHintsCandidates.length > 0) {
      const wordInfo = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: wordInfo.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: wordInfo.endColumn,
      };
      const ranked = rankCandidates(pathHintsCandidates, ctx, String(wordInfo.word ?? ''), rankOptions);
      return { suggestions: ranked.map((item) => toMonacoItem(item, monaco, range)) };
    }
  }

  // Phase 3 — enum-value completion. Fires (like JSON-path) BEFORE the
  // string-bail because the cursor sits inside an unclosed string literal at
  // `WHERE status = '|`. Empty cache → 0 candidates → fall through.
  if (input.connectionId && effectiveSchema) {
    const enumCandidates = collectOracleEnumValueCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: effectiveSchema,
      snapshotTables: augmentedTables,
      aliasBindings: ctx.aliasBindings,
      resolveTable,
    });
    if (enumCandidates.length > 0) {
      const edit = computeEnumValueEdit(model, position, text, offset);
      const ranked = rankCandidates(enumCandidates, ctx, edit.word, rankOptions);
      return {
        suggestions: ranked.map((item) => toMonacoItem(item, monaco, edit.range)),
      };
    }
  }

  if (ctx.insideStringOrComment) return { suggestions: [] };

  const wordInfo = model.getWordUntilPosition(position);
  const typed = String(wordInfo.word ?? '');
  // Cold cache → warm fire-and-forget. Subsequent keystrokes will see the
  // populated snapshot. Same fire-and-forget contract MSSQL / PG / MySQL /
  // SQLite use. Eliminates the previous "schema introspection too slow"
  // skip on the legacy E2E by keeping the hot path zero-IPC.
  if (!snap && input.connectionId && effectiveSchema) {
    void ensureOracleSchema({
      connectionId: input.connectionId,
      schema: effectiveSchema,
    }).catch(() => undefined);
  }

  const cached = input.connectionId ? getCachedOracleSchema(input.connectionId) : undefined;
  const synonyms = cached?.synonyms ?? [];
  const sequences = cached?.sequences ?? [];
  const packages = cached?.packages ?? [];

  const candidates = await buildCandidates({
    ctx,
    text,
    connectionId: input.connectionId,
    database: input.database,
    snapshotTables: augmentedTables,
    snapshotSchemas: snap?.schemas ?? [],
    snapshotForeignKeys: snap?.foreignKeys ?? [],
    looseRels:
      input.connectionId && effectiveSchema
        ? getLooseRelationships(input.connectionId, effectiveSchema)
        : [],
    snapshotRoutines: snap?.routines ?? [],
    snapshotUserTypes: (snap?.userTypes ?? []).map((t) => t.name),
    snapshotObjectTypeAttributes: snap?.objectTypeAttributes ?? {},
    cteBindings: ctx.cteBindings,
    synonyms,
    sequences,
    packages,
    currentSchema: effectiveSchema,
    propertyGraphs: snap?.propertyGraphs ?? [],
  });

  // `USING (▮)` / `ROLLUP(▮)` / Oracle's `SAMPLE (▮)` admit operands or
  // nothing — never the tables and FK-join snippets the full emission adds.
  const scoped = applyPlsqlParenFilter(
    applyIndexTargetFilter(
      applyParenScopeFilter(candidates, text, offset, ctx.statementRange.start, ENGINE),
      text, offset, ctx.statementRange.start, ENGINE,
    ),
    text, offset, ctx.statementRange.start, ENGINE,
  );
  // GROUP BY scope (`context/groupByScope.ts`) — once the statement groups, a
  // bare non-grouped column is ORA-00979 at HAVING / ORDER BY, so the
  // candidate is dropped rather than demoted. Self-gated: HAVING / ORDER BY /
  // post-GROUP-BY select list only, and only when the GROUP BY clause parsed
  // with confidence (a `WITHIN GROUP (ORDER BY ▮` caret sits inside a paren
  // and keeps the full column set — script.58 §1). Wired at the one place
  // every arm's candidates converge, so the clause arm, the predicate-head
  // arm and the value-slot refusals are covered by one call. Closes oracle
  // script.57 §1.
  const grouped = applyGroupByScopeFilter(scoped, {
    text,
    offset,
    statementStart: ctx.statementRange.start,
    statementEnd: ctx.statementRange.end,
    engineId: ENGINE,
    positionKind: ctx.positionKind,
  });
  const withDocs = enrichCandidatesWithDocs(grouped, {
    aliasBindings: ctx.aliasBindings,
    snapshotTables: augmentedTables,
    foreignKeys: snap?.foreignKeys ?? [],
    fold: foldIdentifier,
  });
  const ranked = rankCandidates(withDocs, ctx, typed, rankOptions);

  const range = {
    startLineNumber: position.lineNumber,
    startColumn: wordInfo.startColumn,
    endLineNumber: position.lineNumber,
    endColumn: wordInfo.endColumn,
  };
  const suggestions = ranked.map((item) => toMonacoItem(item, monaco, range));
  return { suggestions };
}

function buildHintSuggestions(
  monaco: any,
  range: { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number },
): any[] {
  const CompletionItemKind = monaco?.languages?.CompletionItemKind;
  const insertAsSnippet = monaco?.languages?.CompletionItemInsertTextRule?.InsertAsSnippet;
  const numericKind = CompletionItemKind?.Snippet ?? 0;
  return ORACLE_HINT_NAMES.map((hint) => ({
    label: hint.name,
    kind: numericKind,
    detail: hint.detail,
    documentation: '',
    insertText: hint.takesAliasArg ? `${hint.name}($0)` : hint.name,
    ...(hint.takesAliasArg && insertAsSnippet ? { insertTextRules: insertAsSnippet } : {}),
    sortText: `6|${hint.name}`,
    filterText: hint.name,
    range,
  }));
}

// Fold in-buffer CREATE TABLE rows into the snapshot table set, skipping any
// that collide (by schema.name) with a table the snapshot already carries.
// Mirrors the pgsql/sqlite/mysql merge — see inBufferTables.ts (1c parity).
function mergeInBufferTables(
  snapshot: ReadonlyArray<SnapshotTable>,
  inBuffer: ReadonlyArray<InBufferTable>,
): ReadonlyArray<SnapshotTable> {
  if (inBuffer.length === 0) return snapshot;
  const keyOf = (schema: string | null | undefined, name: string): string =>
    `${(schema ?? '').toLowerCase()}.${name.toLowerCase()}`;
  const known = new Set<string>();
  for (const t of snapshot) known.add(keyOf(t.schema, t.name));
  const merged: SnapshotTable[] = [...snapshot];
  for (const t of inBuffer) {
    const schema = t.qualifier ?? null;
    if (known.has(keyOf(schema, t.name))) continue;
    merged.push({
      name: t.name,
      schema: schema ?? undefined,
      kind: t.isTemporary ? 'temporary-table' : 'table',
      columns: t.columns.map((c) => ({ name: c.name, dataType: c.dataType })),
    });
  }
  return merged;
}

type SnapshotTable = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: string;
  readonly columns: ReadonlyArray<{
    readonly name: string;
    readonly dataType: string;
    // Carried by the bus snapshot's ColumnMeta; read by the pkFirst mode of
    // the REFERENCES / constraint column-list emission.
    readonly isPrimaryKey?: boolean;
    readonly jsonClassification?: import('@/features/sql-resolver/types/schema').JsonClassification;
    // Carried by the bus snapshot's ColumnMeta (T18b, D13) — read to build
    // the NULL keyword legality of a typed INSERT VALUES element.
    readonly nullable?: boolean;
  }>;
  // Indexes on the relation — the index-NAME vocabulary of `DROP INDEX ▮`
  // (see `IndexMeta`; undefined on engines whose adapter does not source it).
  readonly indexes?: ReadonlyArray<{
    readonly name: string;
    readonly columns: readonly string[];
    readonly isUnique: boolean;
    readonly isPrimary: boolean;
  }>;
};

type SnapshotRoutine = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: 'procedure' | 'function' | 'aggregate' | 'window';
  /** Return type (bus `RoutineMeta.returns`) — drives value-slot routine
   *  tiering (T11); absent on procedures / objects without one. */
  readonly returns?: string;
};

type BuildArgs = {
  readonly ctx: ReturnType<typeof deriveCompletionContext>;
  readonly text: string;
  readonly snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly snapshotSchemas: readonly string[];
  readonly snapshotForeignKeys: readonly FkEdge[];
  readonly looseRels: readonly LooseRelationship[];
  readonly snapshotRoutines: readonly SnapshotRoutine[];
  /** User-defined type names surfaced at data-type slots (ADDRESS_TYPE). */
  readonly snapshotUserTypes: readonly string[];
  /** OBJECT-type attribute members keyed by uppercase type name — drives
   *  `t.objcol.▮` attribute completion (ADDRESS_TYPE → street/city/…). */
  readonly snapshotObjectTypeAttributes: Record<
    string,
    readonly { readonly name: string; readonly dataType: string }[]
  >;
  readonly cteBindings: readonly CteBinding[];
  readonly synonyms: readonly OracleSchemaSynonym[];
  readonly sequences: readonly OracleSchemaSequence[];
  readonly packages: readonly OracleSchemaPackage[];
  readonly currentSchema?: string;
  /** SQL/PGQ property graphs (`GRAPH_TABLE( ▮` targets) with labels/properties.
   *  Empty when the server has none / is pre-23ai — the graph gate then stays
   *  inert and completion is byte-identical to the non-graph path. PGQ-function
   *  RU version gating is applied downstream by the orchestrator's
   *  version-gating post-process (which reads the bus snapshot's serverVersion
   *  against the version-metadata catalog), so the resolver emits plain
   *  `system-function` candidates and does not gate them in-line — decorating
   *  here would double-apply the badge/demote against that post-process. */
  readonly propertyGraphs: readonly PropertyGraphMeta[];
  /** For MetaInfo lookups (declared-JSON columns) — may be empty in tests. */
  readonly connectionId?: string;
  readonly database?: string;
};

async function buildCandidates(args: BuildArgs): Promise<CompletionCandidate[]> {
  const { ctx, snapshotTables, snapshotSchemas, snapshotForeignKeys } = args;
  const out: CompletionCandidate[] = [];

  // Compound-keyword remainder — when the caret is attached to the second (or
  // third) word of a multi-word keyword (`CURRENT R▮`, `UNBOUNDED P▮`,
  // `NEXT V▮`, `REFRESH ON D▮`, `READS S▮`, `ON DUPLICATE K▮`), offer what is
  // left of that keyword. Runs FIRST so the keyword-only short-circuits below
  // (which return `out` as it stands) carry it; additive, so a caret whose head
  // word is really an identifier keeps everything else it had. See
  // `compoundKeywordRemainder.ts`.
  emitCompoundKeywordRemainders(
    out,
    ENGINE,
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.positionKind,
  );

  // O2 / Task 20 — SQL/PGQ GRAPH_TABLE completion. A text-walk gate (like the
  // `isOracleDataTypePosition` / window-frame gates below) rather than a
  // `positionKind` branch: inside a canonical `FROM GRAPH_TABLE( … )` the
  // clause machine names the slot `'from'` (and `elem.▮` short-circuits to
  // `'dotted-access'` in deriveContext before the slot seam runs), so a
  // CPK-only dispatch would only catch the degenerate no-FROM form. Running
  // the SAME Task-18 `classifyGraphSlot` here catches every form. Returns null
  // (falls through) when there are no property graphs or the cursor is not
  // inside a `GRAPH_TABLE (`, so non-PGQ completion is byte-identical.
  const graphCandidates = collectOracleGraphCandidates(args);
  if (graphCandidates) return graphCandidates;

  // Oracle PL/SQL procedural local-variable completion (ROUND2-C1.2).
  // When the cursor is inside a CREATE [OR REPLACE] PROCEDURE/FUNCTION/
  // PACKAGE BODY/TRIGGER … IS|AS body, surface the variable declarations
  // visible at the cursor position (tier 1). Runs before the DML position-
  // kind branching so procedural locals are merged with schema-based candidates.
  // The call is guarded inside `emitOracleProceduralLocalCandidates` —
  // it degrades to an empty list when the PL/SQL ANTLR bundle is not yet
  // loaded or the parse fails, so DML candidates still flow.
  //
  // Slot gate (parity with mysqlResolver isNonExpressionSlot): at FROM table
  // list and DDL column / type / constraint slots, parameters and locals are
  // not legal identifiers. At a dotted-access slot we want the dotted-base's
  // members (record fields, package routines), never the per-routine locals.
  // Pinned by oracle script.33 §4/§5/§8, script.36 §4/§6 where pre-fix
  // headOfActual surfaced `p_*` / `v_*` ahead of the expected column set.
  if (
    isInsideOracleProceduralBody(args.text, ctx.offset) &&
    !isOracleNonExpressionSlot(ctx.positionKind)
  ) {
    await emitOracleProceduralLocalCandidates(args.text, ctx.offset, out);
    // F5.3 Wave-4 R-bucket — predefined PL/SQL exception names at the
    // `WHEN ` slot inside an EXCEPTION block (Script 33 §10). Gated to the
    // procedural body so we don't surface them in plain DML positions; the
    // slot detector additionally requires a prior `EXCEPTION` token in
    // statement scope, so `CASE WHEN …` doesn't trigger them.
    if (
      isAtPlsqlExceptionWhenSlot(args.text, ctx.offset, ctx.statementRange.start)
    ) {
      emitPlsqlExceptionNames(out);
    }
  }

  const positionKind = ctx.positionKind;

  // PIVOT / UNPIVOT interior — when the caret sits inside a pivoted
  // subquery's `PIVOT (…)` clause, the aggregate-argument and `FOR <col>`
  // slots reference the PRE-pivot projection of the inner relation. Emit
  // those columns and short-circuit. Closes oracle script.52 §1/§2.
  if (ctx.dottedBase === undefined || ctx.dottedBase.parts.length <= 1) {
    const pivotHost = ctx.aliasBindings.find(
      (b) =>
        b.pivotRange !== undefined &&
        ctx.offset > b.pivotRange.start &&
        ctx.offset < b.pivotRange.end,
    );
    if (pivotHost && pivotHost.bodyRange) {
      const names = inferSubqueryColumns({
        source: args.text,
        bodyRange: pivotHost.bodyRange,
        engineId: 'oracle',
        snapshotTables,
        cteBindings: ctx.cteBindings,
      });
      if (names.length > 0) {
        pushOracleSubqueryColumns(out, names, pivotHost.alias);
        return out;
      }
    }
  }

  // The pivoted relation may be a plain table (`FROM orders PIVOT (…)`), which
  // has no subquery body for the block above to infer a projection from. The
  // `FOR ▮` slot there names a column of the bound relation; the clause machine
  // reports it as a FROM tail, so the relation keywords answered it instead.
  if (
    ctx.aliasBindings.length > 0 &&
    isPivotForColumnPosition(args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE))
  ) {
    emitAllBoundAliasColumns(
      out, ctx.aliasBindings, snapshotTables, ctx.cteBindings, args.synonyms, args.text,
    );
    return out;
  }

  if (positionKind === 'dotted-access' && ctx.dottedBase) {
    emitDottedAccess(out, args);
    return out;
  }

  // `ROLLBACK TO ▮` / `RELEASE ▮` — a savepoint NAME slot. The name is
  // transaction state the buffer itself declared, so no schema emission can
  // reach it; before this the caret fell to `unknown` and answered with the
  // table list. Shared across all five engines (each spells the statements
  // differently — see `savepointSlot.ts`). Closes sqlite script.50 §9/§10.
  if (emitSavepointNameCandidates(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE)) {
    return out;
  }

  // Index-NAME slot. `DROP INDEX ▮` names an INDEX, not a relation, but
  // the caret falls to 'unknown' and answered with the table list. Shared
  // recognizer + emitter (`indexNameSlot.ts`); it claims the caret only when
  // the snapshot actually carries index metadata for the engine, so an engine
  // whose adapter has not populated `TableMeta.indexes` yet is unchanged.
  if (emitIndexNameCandidates(out, {
    text: args.text,
    offset: ctx.offset,
    statementStart: ctx.statementRange.start,
    statementEnd: ctx.statementRange.end,
    engine: ENGINE,
    tables: args.snapshotTables,
    format: formatIdentifier,
  })) {
    return out;
  }

  // `ALTER TABLE t RENAME TO ▮` — the NEW name. Every name the schema could
  // offer is a name the rename would collide with, so the slot is closed.
  // Shared with the other RENAME-capable engines (see `renameTargetSlot.ts`).
  if (isAtRenameTargetSlot(args.text, ctx.offset, ctx.statementRange.start, ENGINE)) {
    return out;
  }

  // Value-slot plan T11 — scalar-value branch (oracle clone of the T8 pgsql
  // exemplar). Reached ONLY when the engine's capability table (or the T5b
  // test setter) admits the slot's role; LIVE in production as of the T13d
  // flip (`valueSlotCapabilities.ts`). The shared policy owns the candidate
  // set; Oracle built-in functions stay engine-emitted at their tier.
  if (positionKind === 'scalar-value' && ctx.valueSlot) {
    const { adapter, scope } = buildOracleValueSlotAdapter({
      slot: ctx.valueSlot,
      text: args.text,
      aliasBindings: ctx.aliasBindings,
      cteBindings: ctx.cteBindings,
      snapshotTables,
      snapshotRoutines: args.snapshotRoutines,
      snapshotForeignKeys,
      sequences: args.sequences,
      packages: args.packages,
      // P8c (C1 flip-gate) — synonym-bound alias bindings resolve to their
      // target's columns inside the adapter (previously the adapter never
      // received the synonym list, so those bindings contributed nothing).
      synonyms: args.synonyms,
      // C2 P1-1 — virtual data-dictionary fallback: same lookup as
      // `emitAliasColumns`'s final fall-through over
      // ORACLE_SYSTEM_TABLE_COLUMNS (keyed by folded relation name alone).
      virtualColumnsFor: (binding) => {
        const relationFolded = foldIdentifier(binding.relation);
        return ORACLE_SYSTEM_TABLE_COLUMNS[relationFolded]?.map((name) => ({ name }));
      },
    });
    await emitValueSlotCandidates(out, ctx.valueSlot, adapter, scope);
    emitBuiltinFunctions(out);
    // C2 P2-4 — hierarchical-query pseudo-columns (LEVEL, CONNECT_BY_ROOT,
    // SYS_CONNECT_BY_PATH, ...) are legal scalars at a comparison RHS inside
    // a CONNECT BY / START WITH statement; the clause path's emission sits
    // after this branch's return, so call the same guarded emitter here.
    emitHierarchicalCandidatesIfActive(out, args);
    // T13d no-worse parity (F5.3-R, clone of pgsqlResolver's T13a /
    // mssqlResolver's T13b / mysqlResolver's T13c fix) — the clause path
    // surfaces SELECT-list projection aliases at HAVING positions. A
    // comparison-RHS slot that overrode the HAVING clause must keep them.
    // Recompute the clause with the same machine `deriveCompletionContext`
    // used (the slot path drops it); WHERE/JOIN-ON slots never gain aliases
    // — projections aren't computed yet at those points in SQL evaluation
    // order.
    const scalarClause = computeClauseContext(
      args.text, ctx.statementRange.start, ctx.offset, ENGINE,
    )?.clause;
    if (scalarClause === 'having') {
      emitSelectListAliasCandidates(
        out, args.text, ctx.statementRange, ctx.offset, ENGINE, 'having',
        (name) => formatIdentifier(name),
      );
    }
    return out;
  }

  // Predicate-head plan P6c/P8c — predicate-head branch, LIVE in production
  // since the P8c flip (oracle carries all three head roles in
  // `predicateHeadCapabilities.ts`). Reached when the capability gate admits
  // the head's role. Shape-mirrors the scalar-value branch above: shared
  // policy owns the candidate set, Oracle built-in functions stay
  // engine-emitted at their existing tier (the PH-D4 builtin exemption —
  // they carry no return metadata to tier against, exactly as the scalar
  // branch ships them). Two oracle-specific C1 flip-gates live in the
  // adapter: synonym-bound bindings resolve to target columns (in BOTH
  // modes), and sequences are suppressed in this no-LHS mode only
  // (ORA-02287 — `seq.NEXTVAL` is illegal inside WHERE/HAVING/ON).
  if (positionKind === 'predicate-head' && ctx.predicateHead) {
    const { adapter } = buildOracleValueSlotAdapter({
      // No `slot`: PH-D3's no-LHS mode. The overload makes this a distinct
      // signature, so a scalar-value caller cannot reach it by dropping its
      // slot.
      text: args.text,
      aliasBindings: ctx.aliasBindings,
      cteBindings: ctx.cteBindings,
      snapshotTables,
      snapshotRoutines: args.snapshotRoutines,
      snapshotForeignKeys,
      sequences: args.sequences,
      packages: args.packages,
      synonyms: args.synonyms,
      // C2 P1-1 — virtual data-dictionary fallback: same lookup as
      // `emitAliasColumns`'s final fall-through over
      // ORACLE_SYSTEM_TABLE_COLUMNS (keyed by folded relation name alone).
      virtualColumnsFor: (binding) => {
        const relationFolded = foldIdentifier(binding.relation);
        return ORACLE_SYSTEM_TABLE_COLUMNS[relationFolded]?.map((name) => ({ name }));
      },
    });
    await emitPredicateHeadCandidates(out, { head: ctx.predicateHead }, adapter);
    emitBuiltinFunctions(out);
    // Post-policy parity (P1-sol F8) — the same engine-specific follow-up the
    // scalar-value branch replays: hierarchical-query pseudo-columns (LEVEL,
    // CONNECT_BY_ROOT, SYS_CONNECT_BY_PATH, ...) are legal at a head inside a
    // CONNECT BY / START WITH statement, and the clause path's emission sits
    // after this branch's return, so call the same guarded emitter here.
    emitHierarchicalCandidatesIfActive(out, args);
    // Post-policy parity (P1-sol F8) — the clause path surfaces SELECT-list
    // projection aliases at HAVING positions. A head slot that overrode the
    // HAVING clause must keep them. Recompute the clause with the same
    // machine `deriveCompletionContext` used (the slot path drops it);
    // WHERE/JOIN-ON heads never gain aliases — projections aren't computed
    // yet at those points in SQL evaluation order.
    const headClause = computeClauseContext(
      args.text, ctx.statementRange.start, ctx.offset, ENGINE,
    )?.clause;
    if (headClause === 'having') {
      emitSelectListAliasCandidates(
        out, args.text, ctx.statementRange, ctx.offset, ENGINE, 'having',
        (name) => formatIdentifier(name),
      );
    }
    return out;
  }

  // §4a — named-window completion after `OVER `/`OVER (`. Additive (tier 1,
  // no short-circuit): the WINDOW-clause names rank above the position's
  // generic keyword/column vocabulary, which the pipeline still emits below.
  // Oracle 21c+ has the `WINDOW w AS (…)` clause (decision 7).
  if (!ctx.insideStringOrComment) {
    emitNamedWindowCandidates({
      out,
      text: args.text,
      offset: ctx.offset,
      statementStart: ctx.statementRange.start,
      statementEnd: ctx.statementRange.end,
      engine: 'oracle',
    });
  }

  // F5.3 Wave-4 — PL/SQL body context: the grammar can classify a
  // `DBMS_MVIEW.|REFRESH(…)` position as `'unknown'` (PL/SQL syntax is
  // outside the SQL grammar's reach) while the text-walk in deriveContext
  // still extracts the dotted base. When the cursor sits at a one-part
  // dotted-base AND the base resolves to a known package (snapshot or
  // built-in registry), surface the package members anyway. Closes
  // goals/autocomplete-coverage oracle script.46 §4.
  if (ctx.dottedBase && ctx.dottedBase.parts.length === 1) {
    const baseFolded = foldIdentifier(ctx.dottedBase.parts[0]!);
    const builtin = ORACLE_BUILTIN_PACKAGE_MEMBERS[baseFolded];
    if (builtin) {
      out.push(...buildOracleBuiltinPackageMemberCandidates(baseFolded, builtin));
      return out;
    }
    const userPkg = args.packages.find((p) => p.name.toUpperCase() === baseFolded);
    if (userPkg) {
      emitPackageMembers(out, userPkg, args);
      return out;
    }
  }

  // F5.3 Wave-4 — Oracle data-type slot (CREATE TABLE column-type, ALTER
  // TABLE ADD/MODIFY, CREATE FUNCTION param / RETURN, CAST AS, PL/SQL
  // DECLARE). The grammar classifies these slots as `'unknown'`; the narrow
  // text-walk in `isOracleDataTypePosition` recognises them by surface
  // keywords. When matched, surface the database's user-defined types
  // (`ADDRESS_TYPE`, projected onto `snapshotUserTypes` from `ALL_TYPES`)
  // followed by the curated built-in type list, then short-circuit. Closes
  // oracle gaps script.29 §1 (user-defined OBJECT types) + partial script.33
  // §1 (the %TYPE / %ROWTYPE suffix lives in a separate slot — already handled
  // by the bare-table dotted-access fallback).
  // The slot seam also names this position (`ALTER TABLE t ADD col ▮`,
  // `MODIFY col ▮`) — honour it alongside the surface text-walk, which does not
  // cover the ALTER TABLE column-type shapes.
  if (
    positionKind === 'data-type' ||
    isOracleDataTypePosition(args.text, ctx.offset, ctx.statementRange.start)
  ) {
    // A function's `RETURN ▮` additionally accepts the cursor types, which are
    // illegal at a column-type slot — so they ride along only here, ahead of
    // the scalar list (a caret at a RETURN type is very often a ref-cursor
    // function). Closes oracle script.43 §2.
    if (isOracleRoutineReturnTypeSlot(args.text, ctx.offset, ctx.statementRange.start)) {
      // `orderHint` is what keeps them first: within a tier the ranker falls
      // back to alphabetical, which buried `SYS_REFCURSOR` behind every
      // scalar and user type starting with an earlier letter.
      getOracleCursorTypes().forEach((cursorType, index) => {
        out.push({
          label: cursorType,
          insertText: cursorType,
          isSnippet: false,
          kind: 'keyword',
          tier: 1,
          orderHint: index,
          detail: 'PL/SQL cursor type',
        });
      });
    }
    emitOracleDataTypeCandidates(out, args.snapshotUserTypes);
    if (isOraclePlsqlDataTypePosition(args.text, ctx.offset, ctx.statementRange.start)) {
      emitOraclePlsqlTypePatternCandidates(out);
    }
    return out;
  }

  if (positionKind === 'ddl-target-table-column') {
    // `CREATE OR REPLACE TRIGGER … UPDATE OF ▮ ON <tbl>` — the restricted
    // column list, whose owning table is named by the `ON` clause AFTER the
    // caret. Closes oracle script.36 §2.
    const trigger = findTriggerOnTableAhead(args.text, ctx.offset, ctx.statementRange.end);
    if (trigger) {
      emitOracleTargetTableColumns(
        out,
        {
          // The written tokens: a delimited `"Orders"` keeps its case.
          schema: trigger.schemaToken ? foldIdentifier(trigger.schemaToken) : null,
          name: foldIdentifier(trigger.nameToken),
        },
        snapshotTables,
      );
    }
    return out;
  }

  // F5.3 Wave-4 — DDL slot dispatch (mirrors sqlite/pgsql/mysql resolvers).
  if (positionKind === 'ddl-index-column') {
    const target = findOracleIndexTargetTable(args.text, ctx.statementRange.start, ctx.offset);
    if (target) emitOracleTargetTableColumns(out, target, snapshotTables);
    // Function-based indexes (`CREATE INDEX ix ON t (UPPER(email))`) make the
    // built-in functions part of this slot's vocabulary — at their usual
    // tier 8, below the target table's columns. Cross-engine parity with the
    // sqlite / pgsql / mysql index-expression slots.
    emitBuiltinFunctions(out);
    return out;
  }
  if (positionKind === 'ddl-create-object-name') {
    // `CREATE [OR REPLACE] VIEW|TABLE|PACKAGE [BODY]|… ▮` — the NEW object's
    // name. Only a SCHEMA can precede it, so emit the schemas the snapshot
    // knows and nothing else. In Oracle a schema IS a user, and a bare-named
    // object is created in the CONNECTED user's schema — the resolver already
    // carries that as `currentSchema` (`ALTER SESSION SET CURRENT_SCHEMA`
    // override, else the connection's user), so it leads the tier. When the
    // session carries neither, no default is promoted and the list stays
    // alphabetical. No object-type exception applies here (unlike MSSQL / PG /
    // MySQL): Oracle accepts `schema.index_name` and `schema.trigger_name`
    // too — see `createObjectNamespaces.ts`.
    emitCreateObjectNamespaces(out, snapshotSchemas, {
      defaultName: args.currentSchema,
      kind: 'schema',
      detail: 'Schema',
      format: formatIdentifier,
    });
    return out;
  }
  if (positionKind === 'ddl-fk-target') {
    // A FK can only reference a base table, never a view — exclude views and
    // materialized views from the REFERENCES target slot.
    const fkTargetTables = snapshotTables.filter(
      (t) => t.kind !== 'view' && t.kind !== 'materialized-view',
    );
    emitTablesAndSchemas(out, fkTargetTables, snapshotSchemas,
      ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, fkTargetTables)
          : new Set<string>(),
    );
    // I1 parity (mirrors the dotted path) — `FOREIGN KEY (product_id)
    // REFERENCES ▮`: the FK source-column stems name the likely target table;
    // lift it above the alphabetical listing. (Plural-tolerant stem matching
    // lands in G4 so `product_id` reaches a `products` table.)
    applyReferencesTargetPromotion(
      out,
      findFkSourceColumnStems(args.text, ctx.offset, ctx.statementRange.start),
    );
    return out;
  }
  if (positionKind === 'ddl-references-column-list') {
    const target = findOracleReferencesTargetTable(args.text, ctx.statementRange.start, ctx.offset);
    if (target) {
      emitOracleTargetTableColumns(out, target, snapshotTables, { pkFirst: true });
    }
    return out;
  }
  if (positionKind === 'ddl-alter-table-constraint-column-list') {
    const target = findOracleAlterTableTarget(args.text, ctx.statementRange.start, ctx.offset);
    if (target) {
      emitOracleTargetTableColumns(out, target, snapshotTables, { pkFirst: true });
    }
    return out;
  }
  if (positionKind === 'ddl-create-trigger-timing') {
    // Cursor at `CREATE [OR REPLACE] TRIGGER <name> |` — emit the timing
    // vocabulary. Oracle accepts `BEFORE` / `AFTER` / `INSTEAD OF`
    // (the last is restricted to view triggers; surface it anyway since
    // the slot syntax accepts it).
    for (const kw of ['BEFORE', 'AFTER', 'INSTEAD OF']) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // F5.3 Wave-4 — Oracle window-frame slot dispatch. Independent of
  // positionKind. Closes goals/autocomplete-coverage oracle script.54 §3/§4.
  if (isOracleFrameStartPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of ORACLE_FRAME_START_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }
  if (isOracleFrameEndPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of ORACLE_FRAME_END_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // F5.3 Wave-4 — Oracle CREATE VIEW `WITH |` options slot. Independent of
  // positionKind. Closes goals/autocomplete-coverage oracle script.45 §3/§5.
  if (isOracleViewWithOptionPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of ORACLE_VIEW_WITH_OPTION_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // F5.3 Wave-4 — Oracle MATERIALIZED VIEW `REFRESH |` slot. Independent
  // of positionKind. Closes goals/autocomplete-coverage oracle script.32 §5.
  if (isOracleMvRefreshPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of ORACLE_MV_REFRESH_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // F5.3 Wave-4 — Oracle FK referential-action slot `ON DELETE|UPDATE |`.
  // The grammar classifies as `'unknown'`; gate by the text-walk regex.
  // Closes goals/autocomplete-coverage oracle script.30 §5.
  if (isOracleFkActionPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of ORACLE_FK_ACTION_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // F5.3 Wave-4 — `DROP TABLE <name> |` trailing-keyword slot. Independent
  // of positionKind (the grammar classifies this as `'from'` or `'unknown'`
  // depending on the trailing partial-keyword token), the text-walk regex is
  // the gate. Closes goals/autocomplete-coverage oracle script.38 §5.
  if (isOracleDropTableTrailingPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of ORACLE_DROP_TABLE_TRAILING_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // I3 parity — INSERT target slot (`INSERT INTO ▮`). The grammar lumps this
  // under `values`/`unknown`, so the INSERT body's bound-alias columns would
  // otherwise bury the relation names. Fire only once INTO has been typed
  // (`!needsInto`) — the bare `INSERT ▮` tail is ambiguous with the INSERT
  // modifier slot, so leave it to the keyword flow.
  const insertSlot = isAtInsertTargetSlot(args.text, ctx.offset, ctx.statementRange.start);
  if (insertSlot && !insertSlot.needsInto) {
    emitTablesAndSchemas(out, snapshotTables, snapshotSchemas,
      ctx.cteBindings.length === 0
        ? computeBufferReferencedTableKeys(args.text, snapshotTables)
        : new Set<string>(),
    );
    emitSynonymsAsRelations(out, args.synonyms);
    return out;
  }

  if (positionKind === 'from') {
    // Sub-position: cursor after a completed relation / alias (mirrors the
    // MSSQL resolver). Another bare relation here would be a syntax error;
    // emit only the grammatically-legal continuation keywords from the shared
    // legal-next-keyword model, ordered by popularity.
    const fromSlot = classifyNextKeywordSlot(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
      positionKind,
      ctx.aliasBindings,
      ENGINE,
    );
    if (
      fromSlot &&
      (fromSlot.slot === 'after-from-relation-plain' || fromSlot.slot === 'after-join-target')
    ) {
      // Generated table-alias candidate leads the list (orderHint 0); the
      // continuation keywords shift to orderHint 1+. See aliasSuggestions.ts.
      // Oracle note: the candidate inserts the bare alias (no `AS` — Oracle
      // forbids AS for table aliases).
      const aliasEmitted = emitAliasCandidate(out, {
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        slot: fromSlot,
        aliasBindings: ctx.aliasBindings,
        keywords: ORACLE_KEYWORD_SET,
      });
      emitLegalNextKeywords(out, fromSlot, aliasEmitted ? 1 : 0);
      return out;
    }
    // Any OTHER slot the classifier names at a `from` position is a boundary
    // where a relation is illegal — a set operator starting a new query branch
    // (`… FROM t UNION ▮`), a shape keyword. Emit its keywords and stop, rather
    // than falling through to the table list below.
    //
    // A slot with an EMPTY list (the word is a construct in some other dialect,
    // e.g. a table legally named `tablesample` in MySQL) counts as DECLINED —
    // it must reach the after-relation keyword fallback below, not the bare
    // relation list, which is a syntax error at a completed relation.
    const fromSlotEmitted = !!fromSlot && emitLegalNextKeywords(out, fromSlot);
    if (fromSlotEmitted) return out;
    // Null-fallback: the classifier can decline an after-relation slot it can't
    // categorize confidently (e.g. delimited-identifier edge cases). When the
    // broader `isAfterCompletedFromRelation` text-walk still says we're past a
    // completed relation, preserve the pre-classify behavior — emit the curated
    // continuation keywords rather than offering syntactically-illegal tables.
    if (!fromSlotEmitted && isAfterCompletedFromRelation(args.text, ctx.offset, ctx.statementRange.start)) {
      emitKeywords(out, ORACLE_NEXT_AFTER_FROM_RELATION);
      return out;
    }
    emitTablesAndSchemas(
      out,
      snapshotTables,
      snapshotSchemas,
      unionTableKeys(
        computeFkLinkedTableKeys(ctx.aliasBindings, snapshotTables, snapshotForeignKeys, oracleBindingResolver(snapshotTables)),
        ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
      ),
    );
    // S6 — the relation already written at the caret's own binding outranks
    // the other tier-1 (FK-linked / buffer-referenced) tables.
    promoteCaretBindingRelation(out, ctx.aliasBindings, ctx.offset);
    emitDualPseudoTable(out);
    emitSynonymsAsRelations(out, args.synonyms);
    emitCteCandidates(out, ctx);
    if (ctx.aliasBindings.length > 0 && snapshotForeignKeys.length > 0) {
      emitFkJoinSnippets(out, ctx.aliasBindings, snapshotTables, snapshotForeignKeys);
    }
    if (ctx.aliasBindings.length > 0 && args.looseRels.length > 0) {
      emitLooseRelJoinSnippets({
        out,
        bindings: ctx.aliasBindings,
        tables: snapshotTables,
        looseRels: args.looseRels,
        realFks: snapshotForeignKeys,
        engine: ENGINE,
        resolveBinding: oracleBindingResolver(snapshotTables),
      });
    }
    return out;
  }

  if (positionKind === 'ddl-trigger-event') {
    // The slot seam already named this position; only the emission was
    // missing, so the resolver flooded at a caret it knew exactly.
    if (
      emitLegalNextKeywords(out, {
        slot: 'at-trigger-event',
        relationHasAlias: false,
        projectionIsStar: false,
      })
    ) {
      return out;
    }
  }

  if (
    positionKind === 'join-on' ||
    positionKind === 'where' ||
    positionKind === 'having' ||
    positionKind === 'set' ||
    positionKind === 'values' ||
    positionKind === 'returning' ||
    positionKind === 'group-by' ||
    positionKind === 'order-by' ||
    positionKind === 'select-list' ||
    positionKind === 'subquery' ||
    positionKind === 'cte-def' ||
    positionKind === 'limit-offset' ||
    positionKind === 'unknown'
  ) {
    // T18b — typed VALUES element (Docs/AutocompleteValueSlot-Plan.md). When
    // the cursor sits inside a VALUES element whose target column is
    // positively known (explicit column list; findInsertValuesTarget), the
    // resolved-target policy (T18a) replaces the generic values-position
    // emission below and short-circuits — columns/relations are structurally
    // illegal here. NO reclassification (G23): the position stays
    // positionKind 'values'; this is resolver-side dispatch only.
    if (positionKind === 'values') {
      const hit = emitTypedValuesElement(out, args, ctx);
      if (hit) return out;
    }
    // T19 — INSERT RETURNING list, before the INTO (D11: INSERT-context
    // only). Emit ONLY the target table's columns + scalar functions; the
    // relation/keyword flood never serves this position. UPDATE/DELETE
    // RETURNING and the post-INTO bind side fall through unchanged.
    if (positionKind === 'returning') {
      const hit = emitInsertReturningCandidates(out, args, ctx);
      if (hit) return out;
    }
    // Position-aware continuation keywords: when the shared classifier
    // confidently names one of the keyword-only sub-positions (after a
    // completed predicate / GROUP-BY / ORDER-BY item / SELECT projection),
    // emit ONLY the legal ordered keywords and short-circuit so columns /
    // tables / generic keywords don't dilute the boundary. The FROM-relation
    // and join-target slots are handled by the `from` arm above, so exclude
    // them here. A `null` classification (e.g. `WHERE |`, `SELECT a, |`) falls
    // through to today's full emission unchanged.
    const kwSlot = classifyNextKeywordSlot(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
      positionKind,
      ctx.aliasBindings,
      ENGINE,
    );
    // Anywhere inside the row-limiting clause (`LIMIT ▮` … `FETCH FIRST 10 ROWS
    // ONLY ▮`) only integers, the clause's own keywords and in-scope integer
    // operands are legal — relations, columns and the general keyword list are
    // a syntax error at every one of those carets. Emit that and stop; the
    // schema emission below must not run.
    if (kwSlot && isRowLimitSlot(kwSlot.slot)) {
      return buildRowLimitOperandCandidates(out, kwSlot.slot, ENGINE);
    }
    if (
      kwSlot &&
      kwSlot.slot !== 'after-from-relation-plain' &&
      kwSlot.slot !== 'after-join-target'
    ) {
      if (emitLegalNextKeywords(out, kwSlot)) return out;
    }
    if (ctx.aliasBindings.length > 0) {
      emitAllBoundAliasColumns(out, ctx.aliasBindings, snapshotTables, ctx.cteBindings, args.synonyms, args.text);
      // The trigger row qualifiers themselves (`NEW` / `OLD`). Inside a
      // trigger body there is no unqualified row scope, so one of these is the
      // first token the user must type to reach a column — yet only their
      // COLUMNS were ever emitted, never the qualifier.
      emitTriggerPseudoRelationNames(
        out, args.text, ctx.statementRange, ctx.aliasBindings, ENGINE, ctx.offset,
      );
      // `MERGE … WHEN NOT MATCHED THEN INSERT (…) VALUES (▮` — the operands
      // come from the USING source, so the qualified `src.id` form is the one
      // that gets written, and a bare name the TARGET also carries is an
      // ambiguous column reference. Shared policy with the pgsql / mssql
      // MERGE arms (D9 mirror); this resolver supplies the discovery.
      emitOracleMergeValuesColumns(out, args, ctx);
    }
    // F5.3-R-bucket: at the bare `ON (…)` position inside a JOIN or MERGE,
    // surface the bound alias names themselves (`tgt`, `src` for MERGE;
    // `e`, `d` for a self-join) so the user can type `alias.col` from the
    // suggest widget. Without this, the bare position only emits expanded
    // alias columns and tables — the alias labels never appear.
    if (positionKind === 'join-on' && ctx.aliasBindings.length > 0) {
      emitAliasNames(out, ctx.aliasBindings, snapshotTables);
    }
    // T16: the S0 value-slot relation guard (plan: Docs/AutocompleteValueSlot-Plan.md
    // T3/T4) that used to gate `emitTablesAndSchemas` + `emitSynonymsAsRelations`
    // + `emitCteCandidates` below on `!detectComparisonValueContext(...)` is
    // RETIRED — every comparison-value operand in where/having/join-on now
    // classifies via the LIVE scalar-value branch above (comparison-rhs,
    // set-rhs, between-low, between-high; the scalar branch excludes DUAL by
    // design — `oracleValueSlotAdapter.ts`), which returns before this
    // expression arm is ever reached.
    //
    // P8c (Docs/AutocompletePredicateHead-Plan.md): bare predicate HEADS
    // (`WHERE ▮`, `HAVING ▮`, `ON ▮`, `AND/OR/NOT ▮` continuations) no longer
    // reach this arm either — they classify 'predicate-head' and return from
    // the LIVE head branch above, where relations are FORBIDDEN_KINDS
    // (`WHERE <table>` is illegal SQL; synonym names resolve to tables/views
    // and are equally illegal, and sequences are suppressed there per
    // ORA-02287). What still lands here under where/having/join-on is the
    // recognizer's conservative refusals — mid-expression boundaries
    // (`WHERE x ▮`), `WHERE (▮` open-paren heads, CASE WHEN heads, IS-chain
    // territory (PH-D6 v1 gaps) — plus the non-predicate positionKinds of
    // this arm (select-list/unknown/…). For those the full relation/synonym
    // emission below is unchanged by design (the no-worse invariant; the v1
    // gaps are recorded in the P10 gaps sweep). Sequences
    // (`.NEXTVAL`/`.CURRVAL`) and packages/functions were never gated (legal
    // scalars); `emitDualPseudoTable` was never gated either (a single
    // always-visible curated pseudo-relation, not catalog flood) and stays
    // unconditional.
    emitTablesAndSchemas(out, snapshotTables, snapshotSchemas,
      ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
    );
    emitDualPseudoTable(out);
    emitSynonymsAsRelations(out, args.synonyms);
    emitSequencesAsCandidates(out, args.sequences);
    emitPackagesAsCandidates(out, args.packages);
    emitCteCandidates(out, ctx);
    emitHierarchicalCandidatesIfActive(out, args);
    // F33: at expression positions (WHERE/SELECT/HAVING/…) only FUNCTIONS are
    // invokable as `name(...)`. Stored PROCEDURES are reachable only via
    // CALL/EXEC statements, never inside an expression — filter them out so we
    // don't emit non-invokable `proc($0)` snippets here.
    emitRoutines(
      out,
      args.snapshotRoutines.filter((r) => r.kind !== 'procedure'),
      args.currentSchema,
    );
    emitBuiltinFunctions(out);
    // F5.3-R-bucket: surface SELECT-list projection aliases in ORDER-BY
    // (standard SQL) and HAVING (asserted by spec for cross-engine parity).
    // Oracle's engine only resolves aliases in ORDER-BY, but completion
    // coverage matches what the user has typed in scope.
    emitSelectListAliasCandidates(
      out,
      args.text,
      ctx.statementRange,
      ctx.offset,
      'oracle',
      positionKind,
      (name) => formatIdentifier(name),
    );
    // At a PL/SQL declaration-section / body statement-start the declarable +
    // statement-starter keywords are the most likely next token — promote them
    // to tier 1 above the buffer-referenced tables that would otherwise bury
    // them below the virtualized fold. Gated on the procedural-body + statement-
    // start signals so plain SQL sub-positions (and mid-expression slots in the
    // body) are unaffected.
    // CB1.7 — otherwise promote FROM / UNION / INTERSECT / MINUS / FOR to tier 1
    // only at the "select-list, after a completed projection" sub-position
    // (e.g. `SELECT * `, `SELECT col1, col2 `). The other position kinds in
    // this arm (where / set / values / …) keep the un-promoted emission.
    const atPlsqlStatementStart =
      isInsideOracleProceduralBody(args.text, ctx.offset) &&
      isAtProceduralStatementStart(args.text, ctx.offset, ENGINE);
    const nextLikely: ReadonlySet<string> = atPlsqlStatementStart
      ? ORACLE_PLSQL_STATEMENT_STARTERS
      : positionKind === 'select-list' &&
          isAfterCompletedSelectProjection(
            args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE),
          )
        ? ORACLE_NEXT_AFTER_SELECT_PROJECTION
        : new Set<string>();
    emitProjectionWildcard(out, positionKind, ENGINE);
    emitKeywords(out, nextLikely);
    // F5.3 Wave-4 — direction + null-ordering keywords at ORDER BY position.
    // After a completed ORDER BY expression (`first_name <here>`), Oracle
    // accepts `ASC`, `DESC`, `NULLS FIRST`, `NULLS LAST`. The two-token NULLS
    // keywords aren't in the curated single-keyword list, so emit them directly.
    if (positionKind === 'order-by') {
      // After a completed sort key these are the likely next token. At the
      // HEAD of the clause a sort key is still owed, so they stay available —
      // the user may be editing an existing item — but must not outrank the
      // columns the position actually calls for.
      const afterSortKey = isAfterCompletedListItem(
        args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE),
      );
      for (const kw of ORACLE_ORDER_BY_DIRECTION_KEYWORDS) {
        out.push({
          label: kw,
          insertText: kw,
          isSnippet: false,
          kind: 'keyword',
          tier: afterSortKey ? 1 : 7,
          detail: 'SQL keyword',
        });
      }
    }
    // JCS Slice C3: type-aware Oracle JSON operator emission. Native JSON
    // (21c+) and CHECK IS JSON columns surface `JSON_EXISTS`, `JSON_VALUE`,
    // `JSON_QUERY`, `IS JSON`, plus dot-notation entry.
    emitAggregateArgHints(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE);
    emitAggregateTailHints(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE);
    emitTypeAwareJsonOperators(out, args);
    // F5.2-A — drop already-typed columns from the next slot inside an
    // INSERT column-list (`INSERT INTO t (a, |)` must not re-offer `a`).
    // A column may be assigned only once in an UPDATE, so the SET list's
    // already-assigned names are illegal at the next slot.
    filterAlreadyAssignedSetColumns(
      out, args.text, ctx.offset, ctx.statementRange.start, ctx.statementRange.end, ENGINE,
    );
    if (positionKind === 'values') {
      filterAlreadyTypedInsertColumns(out, args, ctx);
    }
    return out;
  }

  emitTablesAndSchemas(out, snapshotTables, snapshotSchemas,
    ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
  );
  emitKeywords(out);
  return out;
}

/**
 * T18b — when the cursor sits inside a VALUES element whose target column is
 * positively known (`findInsertValuesTarget`), resolve the target column in
 * the snapshot (`resolveRelation` — the same schema-scoped lookup the
 * enum/JSON-path phase-3 collectors use) and emit the shared resolved-target
 * policy (T18a) — enum values (tier 1), NULL/DEFAULT keywords (D13), a
 * scalar-subquery snippet, and compat-tiered scalar routines. Returns `false`
 * (caller falls through to the unchanged generic emission) when the element
 * isn't positively typed or the target table/column can't be resolved.
 */
function emitTypedValuesElement(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): boolean {
  // Trigger bodies put a row scope in play (`:NEW.`/`:OLD.`), so columns are
  // NOT structurally illegal at VALUES elements there — the resolved-target
  // premise (`legalSources.columns: false`) fails and the generic emission
  // must serve the position. Pinned by sqlite coverage script.36 §5/§6
  // (cross-engine parity guard here).
  if (findOracleTriggerHostTable(args.text, ctx.offset)) return false;
  const hit = findInsertValuesTarget(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!hit) return false;
  const tbl = resolveRelation(
    args.snapshotTables,
    foldIdentifier(hit.table.name),
    hit.table.schema ? foldIdentifier(hit.table.schema) : null,
  );
  if (!tbl) return false;
  const colFolded = foldIdentifier(hit.columnName);
  const column = tbl.columns.find((c) => foldIdentifier(c.name) === colFolded);
  if (!column) return false;

  const columnMeta: ColumnMeta = {
    name: column.name,
    dataType: column.dataType,
    nullable: column.nullable ?? false,
    isPrimaryKey: column.isPrimaryKey ?? false,
    ordinal: 0,
  };

  const connectionId = args.connectionId;
  // Mirrors `collectOracleEnumValueCandidates`'s own `database` key, which is
  // the resolver's effective-schema (`currentSchema` — `ALTER SESSION SET
  // CURRENT_SCHEMA` override, else the connection default), not the raw
  // connection-default `database` field.
  const database = args.currentSchema;
  const target: ResolvedValueTarget = {
    kind: 'insert-column',
    column: columnMeta,
    coarseType: coarseTypeOfDataType(column.dataType),
    legalSources: { columns: false, keywords: legalValueKeywordsFor(ENGINE, columnMeta) },
    collectEnumValues:
      connectionId && database
        ? () => {
            const tmp: CompletionCandidate[] = [];
            emitEnumValueCandidates(tmp, {
              connectionId,
              database,
              schema: tbl.schema ?? '',
              table: tbl.name,
              column: column.name,
              quote: findValueStringBodyStart(args.text, ctx.offset) === undefined,
              tier: 1,
            });
            return tmp;
          }
        : undefined,
  };

  emitResolvedValueCandidates(out, target, {
    engine: ENGINE,
    collectScalarRoutines: () => collectOracleScalarRoutineDescriptors(args.snapshotRoutines),
  });
  // `ResolvedValueAdapter` (T18a) has no `extraScalarSources` slot — same gap
  // the T9 mssql adapter hit for `@`-locals at the scalar-value branch. Same
  // escape hatch: the resolver emits them itself, reusing the exact private
  // functions the generic 'values' arm already calls (`emitSequencesAsCandidates`
  // / `emitPackagesAsCandidates`, tier 3 — mirrors the T11 adapter's own
  // `extraScalarSources`). Fixes the oracleResolver.script11.trio Gate-D
  // regression (sequences dropped out of the typed VALUES slot). Bare
  // NEXTVAL/CURRVAL are NOT emitted here — Oracle's sequence pseudo-columns
  // are only valid behind a sequence qualifier (`order_seq.NEXTVAL`), and
  // the dotted-access branch serves them once the user picks a sequence and
  // types `.` (C3 sol finding 6; the base resolver likewise emitted them
  // only in dotted contexts).
  emitSequencesAsCandidates(out, args.sequences);
  emitPackagesAsCandidates(out, args.packages);
  // Builtin scalar functions (SYSDATE, SYS_GUID(), …) are legal VALUES
  // elements — same builtins-after-policy shape as the scalar-value branch
  // (C3 sol finding 2).
  emitBuiltinFunctions(out);
  return true;
}

/**
 * T19 — INSERT RETURNING list (RETURNING … INTO :bind — this serves the
 * column side BEFORE the INTO; `findInsertReturningTarget` rejects the bind
 * side). Emits ONLY the resolved target table's columns (tier 1) + scalar
 * routines (tier 3); returns `false` (generic emission serves the position)
 * for non-INSERT heads, unresolved targets, or trigger bodies (`:NEW.`/
 * `:OLD.` row scopes are legal in a trigger's RETURNING expression list).
 */
function emitInsertReturningCandidates(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): boolean {
  if (findOracleTriggerHostTable(args.text, ctx.offset)) return false;
  const hit = findInsertReturningTarget(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!hit) return false;
  const tbl = resolveRelation(
    args.snapshotTables,
    foldIdentifier(hit.name),
    hit.schema ? foldIdentifier(hit.schema) : null,
  );
  if (!tbl) return false;
  for (const c of tbl.columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${tbl.name})`,
    });
  }
  for (const r of collectOracleScalarRoutineDescriptors(args.snapshotRoutines)) {
    out.push({
      label: r.label,
      insertText: r.insertText,
      isSnippet: r.insertText.includes('$'),
      kind: 'routine',
      tier: 3,
      detail: `returns ${r.returnsCoarse}`,
      triggerSignatureHelp: true,
    });
  }
  return true;
}

/** F5.2-A — drop column candidates whose label matches a column already
 *  typed earlier in the same INSERT column-list. No-op outside INSERT
 *  column-list (the helper returns null then). */
function filterAlreadyTypedInsertColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): void {
  const typed = findTypedInsertColumns(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!typed || typed.length === 0) return;
  const typedSet = new Set(typed);
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const c = out[i];
    if (c.kind !== 'column') continue;
    if (typedSet.has(c.label.toLowerCase())) {
      out.splice(i, 1);
    }
  }
}

// ───────────────────────────────────────────────────────────────────────────
// SQL/PGQ GRAPH_TABLE completion (O2 / Task 20)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Build candidates for a cursor inside a `GRAPH_TABLE ( … )` clause, or return
 * null when the cursor is not in one (or the snapshot has no property graphs,
 * so the feature stays inert and non-graph completion is untouched). Reuses
 * the Task-18 `classifyGraphSlot` classifier so the slot decision here is
 * byte-identical to the slot seam's.
 */
function collectOracleGraphCandidates(args: BuildArgs): CompletionCandidate[] | null {
  const { ctx, text, propertyGraphs } = args;
  // No graphs → today's behaviour (byte-identical). Cheap substring gate keeps
  // the tokenizer off the hot path for the overwhelming non-PGQ majority.
  if (propertyGraphs.length === 0) return null;
  const slice = text.slice(ctx.statementRange.start, ctx.offset);
  if (!/graph_table/i.test(slice)) return null;

  const tokens = tokenizeUntil(text, ctx.statementRange.start, ctx.offset, 'oracle');
  const slot = classifyGraphSlot(tokens, ctx.offset);
  if (!slot) {
    // A detached top-level position inside `GRAPH_TABLE (` that owns no
    // vocabulary slot (`GRAPH_TABLE(eg ▮`, `… MATCH (a) ▮`) — surface the one
    // structural keyword the grammar expects next (MATCH / COLUMNS). T18 has
    // `classifyGraphSlot` return null there so graph names aren't mis-offered.
    const kw = graphStructuralKeyword(tokens, ctx.offset);
    return kw ? [graphStructuralKeywordCandidate(kw)] : null;
  }
  const info = findGraphTableInfo(tokens);
  if (!info) return null;

  const out: CompletionCandidate[] = [];
  switch (slot.kind) {
    case 'graph-name':
      emitGraphNames(out, propertyGraphs);
      break;
    case 'graph-label':
      emitGraphLabels(out, args, info);
      break;
    case 'graph-element-property':
      emitGraphElementProperties(out, args, info, slot.elementVariable);
      break;
    case 'graph-columns':
      emitGraphColumns(out, args, info);
      break;
  }
  return out;
}

/** Resolve the property graph named after `GRAPH_TABLE (`. Null when the name
 *  is absent or matches nothing (caller falls back to all graphs). */
function resolveGraphByName(
  graphs: readonly PropertyGraphMeta[],
  name: string | null,
): PropertyGraphMeta | null {
  if (!name) return null;
  return graphs.find((g) => graphIdentEquals(name, g.name)) ?? null;
}

function emitGraphNames(
  out: CompletionCandidate[],
  graphs: readonly PropertyGraphMeta[],
): void {
  for (const g of graphs) {
    out.push({
      label: g.name,
      insertText: formatIdentifier(g.name),
      isSnippet: false,
      kind: 'table',
      tier: 2,
      detail: 'Property graph',
      filterText: g.name,
      // Bounded engine-schema population — survive the untyped cap.
      capExempt: true,
    });
  }
}

/**
 * Vertex/edge labels of the enclosing graph, filtered by whether the cursor's
 * label slot sits in a node pattern `( … )` (vertex labels) or an edge pattern
 * `-[ … ]-` (edge labels). Unknown pattern context → all labels. When the
 * graph name is unresolved, fall back to the union of every graph's labels.
 */
function emitGraphLabels(
  out: CompletionCandidate[],
  args: BuildArgs,
  info: GraphTableInfo,
): void {
  const { ctx, text, propertyGraphs } = args;
  const graph = resolveGraphByName(propertyGraphs, info.graphName);
  const source = graph ? [graph] : propertyGraphs;
  const patternCtx = graphPatternContext(text, info.bodyStart, ctx.offset);
  const seen = new Set<string>();
  for (const g of source) {
    for (const lbl of g.labels) {
      if (patternCtx === 'vertex' && lbl.kind !== 'vertex') continue;
      if (patternCtx === 'edge' && lbl.kind !== 'edge') continue;
      const key = lbl.name.toUpperCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        label: lbl.name,
        insertText: lbl.name,
        isSnippet: false,
        kind: 'table',
        tier: 2,
        detail: lbl.kind === 'edge' ? 'Edge label' : 'Vertex label',
        filterText: lbl.name,
        capExempt: true,
      });
    }
  }
}

/**
 * Properties of the element variable's declared label(s): resolve the variable
 * → its labels via `collectGraphElementVariables` over the graph body, then
 * union the matching labels' properties within the enclosing graph. An
 * unlabeled (or unbound) variable → the union of the graph's properties.
 */
function emitGraphElementProperties(
  out: CompletionCandidate[],
  args: BuildArgs,
  info: GraphTableInfo,
  elementVariable: string | undefined,
): void {
  const { ctx, text, propertyGraphs } = args;
  const graph = resolveGraphByName(propertyGraphs, info.graphName);
  const graphs = graph ? [graph] : propertyGraphs;

  const body = text.slice(info.bodyStart, ctx.offset);
  const vars = collectGraphElementVariables(body, 'oracle');
  const bound = elementVariable
    ? vars.find((v) => graphIdentEquals(elementVariable, v.variable))
    : undefined;
  const wantLabels = bound && bound.labels.length > 0 ? bound.labels : null;

  const seen = new Set<string>();
  for (const g of graphs) {
    for (const lbl of g.labels) {
      if (
        wantLabels &&
        !wantLabels.some((w) => graphIdentEquals(w, lbl.name))
      ) {
        continue;
      }
      for (const prop of lbl.properties) {
        const key = prop.name.toUpperCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          label: prop.name,
          insertText: prop.name,
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: prop.dataType
            ? `${prop.dataType} (${lbl.name} property)`
            : `${lbl.name} property`,
          filterText: prop.name,
          capExempt: true,
        });
      }
    }
  }
}

/**
 * The `COLUMNS ( … )` projection slot: the declared element variables (so the
 * user can project `var.prop`), the PGQ scalar/row functions, and the `AS`
 * alias keyword. PGQ functions ride the standard `system-function` kind so the
 * orchestrator's version-gating post-process decorates the RU-gated ones
 * (MATCHNUM, PATH_NAME, …) from the version-metadata catalog.
 */
function emitGraphColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
  info: GraphTableInfo,
): void {
  const { ctx, text } = args;
  const body = text.slice(info.bodyStart, ctx.offset);
  const seenVar = new Set<string>();
  for (const v of collectGraphElementVariables(body, 'oracle')) {
    const key = v.variable.toUpperCase();
    if (seenVar.has(key)) continue;
    seenVar.add(key);
    out.push({
      label: v.variable,
      insertText: v.variable,
      isSnippet: false,
      kind: 'alias',
      tier: 1,
      detail: 'Graph element variable',
      filterText: v.variable,
    });
  }
  for (const fn of PGQ_FUNCTIONS) {
    out.push({
      label: fn.name,
      insertText: `${fn.name}($0)`,
      isSnippet: true,
      kind: 'system-function',
      tier: 3,
      detail: 'PGQ function',
      filterText: fn.name,
      triggerSignatureHelp: true,
    });
  }
  out.push({
    label: 'AS',
    insertText: 'AS',
    isSnippet: false,
    kind: 'keyword',
    tier: 7,
    detail: 'SQL keyword',
  });
}

function emitDottedAccess(out: CompletionCandidate[], args: BuildArgs): void {
  const { ctx, snapshotTables, cteBindings, synonyms, sequences, packages } = args;
  if (!ctx.dottedBase) return;
  const { parts } = ctx.dottedBase;
  if (parts.length === 0) return;
  const baseFolded = foldIdentifier(parts[0]!);

  if (parts.length === 1) {
    // Alias-dot — bound FROM/JOIN alias resolves to its source relation's
    // columns. Anti-SOUNDEX invariant: ONLY this binding's columns surface.
    const aliasMatch = ctx.aliasBindings.find(
      (b) => foldIdentifier(b.alias) === baseFolded,
    );
    if (aliasMatch) {
      emitAliasColumns(out, aliasMatch, snapshotTables, cteBindings, synonyms, args.text);
      // F5.2-CB5 + S3 — FK/partner-aware column ranking at JOIN ON / WHERE
      // dotted positions (partner affinity → endpoint-pair FKs → name∩+PK).
      applyJoinOnColumnRanking({
        out,
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        sqlEngine: 'oracle',
        bindings: ctx.aliasBindings,
        edges: args.snapshotForeignKeys,
        binding: aliasMatch,
        tables: snapshotTables,
      });
      // Workstream C oracle 18.§1 — `JSON_VALUE(p.▮` lifts the JSON document
      // column: buffer-affinity (columns the buffer already uses as JSON_*
      // first args) ∪ bus-classified ∪ MetaInfo-declared.
      if (isAtJsonFunctionFirstArg(args.text, ctx.offset, ctx.statementRange.start)) {
        applyJsonFunctionArgPromotion(
          out,
          collectKnownJsonColumns(args, aliasMatch, snapshotTables),
        );
      }
      return;
    }
    // CTE alias-dot — surface explicit CTE column list.
    const cteMatch = ctx.cteBindings.find(
      (c) => foldIdentifier(c.name) === baseFolded,
    );
    if (cteMatch) {
      emitCteColumnList(out, cteMatch, {
        source: args.text,
        engineId: 'oracle',
        snapshotTables,
        cteBindings,
      });
      // S3 partner affinity for CTE projections (`ON child.manager_id = ec.▮`
      // wants the FK pair `employee_id`) — name-based, no FK metadata on
      // the CTE itself is needed.
      applyJoinOnColumnRanking({
        out,
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        sqlEngine: 'oracle',
        bindings: ctx.aliasBindings,
        edges: args.snapshotForeignKeys,
      });
      return;
    }
    // Sequence dot — `EMP_SEQ.<here>` surfaces NEXTVAL / CURRVAL only.
    if (sequences.some((s) => s.name.toUpperCase() === baseFolded)) {
      emitSequenceMethods(out);
      return;
    }
    // F5.3 Wave-4 → plan19 §4.6 — buffer-local sequence detection. If the
    // user just typed `CREATE SEQUENCE <name>` earlier in the SAME script
    // (or an earlier statement creates the sequence before referencing it as
    // `name.NEXTVAL`), the snapshot hasn't refreshed yet. Treat the name as
    // a sequence when an earlier statement declares it as one. Statement-
    // scoped and string/comment-safe — token-scanned bounded to
    // `[0, ctx.statementRange.end)`, mirroring pgsql's "tables CREATEd
    // earlier in the same buffer" convention (`pgsqlResolver.ts`). Closes
    // goals/autocomplete-coverage oracle script.44 §2 / §4.
    if (isBufferLocalOracleSequence(args.text, ctx.statementRange.end, baseFolded)) {
      emitSequenceMethods(out);
      return;
    }
    // F5.3 Wave-4 — Oracle built-in SYS packages (DBMS_OUTPUT, DBMS_MVIEW,
    // DBMS_STATS, etc.). Checked BEFORE the user-package list because Oracle
    // introspection sometimes carries an empty stub for SYS packages — the
    // registry has the documented member list those stubs lack. Closes
    // goals/autocomplete-coverage oracle script.46 §4.
    const builtinPkg = ORACLE_BUILTIN_PACKAGE_MEMBERS[baseFolded];
    if (builtinPkg) {
      out.push(...buildOracleBuiltinPackageMemberCandidates(baseFolded, builtinPkg));
      return;
    }
    // Package dot — `<USER_PKG>.<here>` surfaces the package's members only.
    const pkgMatch = packages.find((p) => p.name.toUpperCase() === baseFolded);
    if (pkgMatch) {
      emitPackageMembers(out, pkgMatch, args);
      return;
    }
    // Synonym dotted-access — `EMPS.<here>` resolves to target table's columns.
    const synMatch = findSynonymByName(synonyms, baseFolded);
    if (synMatch) {
      emitSynonymTargetColumns(out, synMatch, snapshotTables);
      // Fall through with no further candidates — synonym alone owns the dotted slot.
      return;
    }
    // Bare table-name dot — `employees.<here>` outside any FROM (e.g.
    // PL/SQL DECLARE `v emp employees%ROWTYPE; v_id employees.employee_id%TYPE`)
    // resolves to that table's columns. F5.3 Wave-4 R-bucket fix for the
    // top dotted-access cohort (oracle script.17/33/34/37/39/42/51).
    const directMatch = snapshotTables.find(
      (t) => foldIdentifier(t.name) === baseFolded,
    );
    if (directMatch) {
      for (const c of directMatch.columns) {
        out.push({
          label: c.name,
          insertText: formatIdentifier(c.name),
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `${c.dataType} (${directMatch.name})`,
        });
      }
      return;
    }
    // PL/SQL `<var> <table>%ROWTYPE` binding — `v_emp_row.|` expands to the
    // bound table's columns. Closes oracle script.33 §4/§5 (`v_emp_row` →
    // EMPLOYEES) and the parallel %ROWTYPE forms in script.17/34/37/39/42/51.
    // The lookup is gated by `isInsideOracleProceduralBody` so a chance
    // identifier collision with a routine-local name outside any routine
    // body doesn't hijack the dotted-access slot.
    if (isInsideOracleProceduralBody(args.text, ctx.offset)) {
      const rowtypeBindings = findOracleProceduralRowtypeBindings(
        args.text,
        ctx.offset,
      );
      const bound = rowtypeBindings.get(baseFolded.toLowerCase());
      if (bound) {
        const targetTable = snapshotTables.find(
          (t) =>
            foldIdentifier(t.name) === bound.tableName.toUpperCase() &&
            (bound.schema === undefined ||
              (t.schema ?? '').toUpperCase() === bound.schema.toUpperCase()),
        );
        if (targetTable) {
          for (const c of targetTable.columns) {
            out.push({
              label: c.name,
              insertText: formatIdentifier(c.name),
              isSnippet: false,
              kind: 'column',
              tier: 1,
              detail: `${c.dataType} (${targetTable.name} via %ROWTYPE)`,
            });
          }
          return;
        }
      }

      // PL/SQL `FOR <rec> IN <cursor-or-inline-select> LOOP` — `<rec>.|`
      // surfaces only the cursor's projected columns (Oracle rejects access
      // to non-projected columns at runtime). Closes oracle script.33.§8
      // (inline FOR-IN with audit_log projection) and script.37.§4 (named
      // cursor `c_employees` with employees projection).
      const forInBindings = findOracleProceduralForInBindings(
        args.text,
        ctx.offset,
      );
      const forInBound = forInBindings.get(baseFolded.toLowerCase());
      if (forInBound) {
        for (const col of forInBound.columns) {
          out.push({
            label: col,
            insertText: formatIdentifier(col),
            isSnippet: false,
            kind: 'column',
            tier: 1,
            detail: 'FOR-IN record column',
          });
        }
        return;
      }
    }

    // `SELF.▮` inside a `CREATE [OR REPLACE] TYPE BODY` — the enclosing OBJECT
    // type's attributes, read from the snapshot's `ALL_TYPE_ATTRS` registry
    // (the same source the `<alias>.<objcol>.▮` path uses). `SELF` binds to no
    // alias, package or schema, so without this the dotted base resolved to
    // nothing. Closes oracle script.39 §3/§4/§5.
    if (baseFolded.toUpperCase() === 'SELF') {
      const typeName = findOracleEnclosingObjectTypeName(args.text, ctx.offset);
      const selfAttrs = typeName
        ? args.snapshotObjectTypeAttributes[typeName]
        : undefined;
      if (selfAttrs && selfAttrs.length > 0) {
        for (const a of selfAttrs) {
          out.push({
            label: a.name,
            insertText: formatIdentifier(a.name),
            isSnippet: false,
            kind: 'column',
            tier: 1,
            detail: `${a.dataType} (${typeName} attribute via SELF)`,
          });
        }
        return;
      }
    }

    // CREATE TRIGGER `:NEW.|` / `:OLD.|` pseudo-relation — bind to the
    // trigger's `ON <table>` target columns. Oracle uses the colon-prefixed
    // `:NEW` / `:OLD` syntax (distinct from MySQL / PG / SQLite, which use
    // bare `NEW` / `OLD`), so the shared `findTriggerHostTable` excludes
    // oracle and a dedicated path is required. Pinned by oracle script.36
    // §4/§6 where `:NEW.updated_at` / `:OLD.amount` resolved to nothing
    // and the spec's full column set was missing.
    const baseUp = baseFolded.toUpperCase();
    if (baseUp === 'NEW' || baseUp === 'OLD') {
      if (isColonPrefixedAtCursor(args.text, ctx.offset, baseUp)) {
        const host = findOracleTriggerHostTable(args.text, ctx.offset);
        if (host) {
          const targetTable = snapshotTables.find(
            (t) =>
              foldIdentifier(t.name) === host.name.toUpperCase() &&
              (host.schema === undefined ||
                (t.schema ?? '').toUpperCase() === host.schema.toUpperCase()),
          );
          if (targetTable) {
            for (const c of targetTable.columns) {
              out.push({
                label: c.name,
                insertText: formatIdentifier(c.name),
                isSnippet: false,
                kind: 'column',
                tier: 1,
                detail: `${c.dataType} (${targetTable.name} via :${baseUp})`,
              });
            }
            return;
          }
        }
      }
    }
    // schema.<here> — emit tables in the named schema.
    emitTablesInSchema(out, snapshotTables, baseFolded);
    // Workstream C parity (mirrors pgsql) — `FOREIGN KEY (category_id)
    // REFERENCES JAMSQL.▮`: the FK source-column stems name the likely target
    // table; lift it above the alphabetical schema listing. No-op outside a
    // REFERENCES tail.
    applyReferencesTargetPromotion(
      out,
      findFkSourceColumnStems(args.text, ctx.offset, ctx.statementRange.start),
    );
    return;
  }

  // OBJECT-type attribute access — `<alias>.<objcol>.▮` where `objcol` is an
  // OBJECT-typed column of the alias's relation (the inline-view-alias
  // workaround Oracle requires for SQL dot-access into an OBJECT column).
  // Resolve the projected column's declared type and surface the type's
  // attributes (ADDRESS_TYPE → street/city/state/zip_code/country). Runs
  // before the SCHEMA.TABLE branch since a bound alias takes precedence over
  // a same-named schema. Closes oracle script.17 §2/§3 and script.53 §1-§3.
  if (parts.length === 2 && emitOracleObjectTypeAttributes(out, args, parts)) {
    return;
  }

  // Two-part `SCHEMA.TABLE.<col>` — match snapshot tables on (schema, name).
  if (parts.length >= 2) {
    const part0Folded = foldIdentifier(parts[0]!);
    const part1Folded = foldIdentifier(parts[1]!);
    const tbl = snapshotTables.find(
      (t) =>
        (t.schema ?? '').toUpperCase() === part0Folded &&
        t.name.toUpperCase() === part1Folded,
    );
    if (tbl) {
      for (const c of tbl.columns) {
        out.push({
          label: c.name,
          insertText: formatIdentifier(c.name),
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `${c.dataType} (${tbl.schema ?? ''}.${tbl.name})`,
        });
      }
    }
  }
}

/**
 * `<alias>.<objcol>.▮` OBJECT-type attribute completion. When `alias` is a
 * bound FROM/JOIN alias whose projected column `objcol` carries a declared
 * type known to the snapshot's OBJECT-type registry, emit that type's
 * attributes (tier 1) and report handled=true. No-op (returns false) when the
 * alias/column/type don't line up, so the caller falls through to the
 * SCHEMA.TABLE branch.
 */
function emitOracleObjectTypeAttributes(
  out: CompletionCandidate[],
  args: BuildArgs,
  parts: readonly string[],
): boolean {
  const registry = args.snapshotObjectTypeAttributes;
  if (!registry || Object.keys(registry).length === 0) return false;
  const aliasFolded = foldIdentifier(parts[0]!);
  const binding = args.ctx.aliasBindings.find(
    (b) => foldIdentifier(b.alias) === aliasFolded,
  );
  if (!binding) return false;
  const colType = resolveAliasColumnDataType(binding, parts[1]!, args);
  if (!colType) return false;
  // Object types carry no length/precision parens, but strip defensively and
  // fold to the uppercase registry key.
  const typeKey = colType.toUpperCase().replace(/\s*\(.*$/, '').trim();
  const attrs = registry[typeKey];
  if (!attrs || attrs.length === 0) return false;
  for (const a of attrs) {
    out.push({
      label: a.name,
      insertText: formatIdentifier(a.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${a.dataType} (${typeKey} attribute)`,
    });
  }
  return true;
}

/**
 * Declared `dataType` of the column named `colName` as projected by `binding`.
 * For a subquery/derived alias the inline-view projection is parsed (carrying
 * the source column's type); for a real table / synonym the snapshot column is
 * read directly. CTE columns carry no type today → undefined.
 */
function resolveAliasColumnDataType(
  binding: AliasBinding,
  colName: string,
  args: BuildArgs,
): string | undefined {
  const colFolded = foldIdentifier(colName);
  if (binding.kind === 'subquery' || binding.kind === 'derived') {
    if (!binding.bodyRange || args.text.length === 0) return undefined;
    const proj = inferSubqueryProjection({
      source: args.text,
      bodyRange: binding.bodyRange,
      engineId: 'oracle',
      snapshotTables: args.snapshotTables,
      cteBindings: args.cteBindings,
    });
    return proj.find((c) => foldIdentifier(c.name) === colFolded)?.dataType;
  }
  if (binding.kind === 'cte') return undefined;
  // Real table or synonym.
  const relationFolded = foldIdentifier(binding.relation);
  const schemaFolded = binding.schema ? foldIdentifier(binding.schema) : null;
  let tbl = resolveRelation(args.snapshotTables, relationFolded, schemaFolded);
  if (!tbl && !schemaFolded) {
    const synMatch = findSynonymByName(args.synonyms, relationFolded);
    if (synMatch) {
      tbl = resolveRelation(
        args.snapshotTables,
        synMatch.targetName.toUpperCase(),
        synMatch.targetSchema.toUpperCase(),
      );
    }
  }
  if (!tbl) return undefined;
  return tbl.columns.find((c) => foldIdentifier(c.name) === colFolded)?.dataType;
}

function emitTablesInSchema(
  out: CompletionCandidate[],
  tables: ReadonlyArray<SnapshotTable>,
  schemaFolded: string,
): void {
  for (const t of tables) {
    if ((t.schema ?? '').toUpperCase() !== schemaFolded) continue;
    out.push({
      label: formatIdentifier(t.name),
      insertText: formatIdentifier(t.name),
      isSnippet: false,
      kind: t.kind === 'view' ? 'view' : 'table',
      tier: 2,
      detail: t.kind === 'view' ? 'View' : 'Table',
      filterText: t.name,
    });
  }
}

function makeTableCandidate(t: SnapshotTable, fkLinked = false): CompletionCandidate {
  // Bare label/insert — Oracle users almost never schema-qualify their own
  // schema's tables, so the unqualified form is canonical. Cross-schema
  // tables surface via `SCHEMA.<table>` once the user types the schema dot.
  // FK-linked tables (an FK edge connects them to a bound relation) promote
  // to tier 1 — the actually-joinable candidates at a JOIN slot.
  return {
    label: t.name,
    insertText: formatIdentifier(t.name),
    isSnippet: false,
    kind: t.kind === 'view' ? 'view' : 'table',
    tier: fkLinked ? 1 : 2,
    detail: t.kind === 'view' ? 'View' : 'Table',
    filterText: t.name,
  };
}

function emitTablesAndSchemas(
  out: CompletionCandidate[],
  tables: ReadonlyArray<SnapshotTable>,
  schemas: readonly string[],
  fkLinkedKeys?: ReadonlySet<string>,
): void {
  // Other schemas at tier 4 (other-visible-schemas slot).
  for (const s of schemas) {
    out.push({
      label: formatIdentifier(s),
      insertText: formatIdentifier(s),
      isSnippet: false,
      kind: 'schema',
      tier: 4,
      detail: 'Schema',
      filterText: s,
    });
  }
  // Tables at tier 2; FK-linked ones at tier 1.
  for (const t of tables) {
    out.push(makeTableCandidate(t, fkLinkedKeys?.has(fkLinkedTableKey(t)) ?? false));
  }
}

/**
 * Always-visible `DUAL` pseudo-table — Oracle's single-row, single-column
 * relation used for scalar expressions (`SELECT 1 FROM DUAL`). Tier 2 so it
 * sorts alongside real tables; users almost always type `DUAL` deliberately
 * so over-emission isn't a concern.
 */
function emitDualPseudoTable(out: CompletionCandidate[]): void {
  out.push({
    label: 'DUAL',
    insertText: 'DUAL',
    isSnippet: false,
    kind: 'table',
    tier: 2,
    detail: 'Single-row table',
    filterText: 'DUAL',
    // Engine-shipped single curated name — must survive the untyped cap even
    // when a huge tier-2 table block ranks it past the boundary (rank.ts).
    capExempt: true,
  });
}

function emitSynonymsAsRelations(
  out: CompletionCandidate[],
  synonyms: readonly OracleSchemaSynonym[],
): void {
  for (const s of synonyms) {
    const target = `${s.targetSchema}.${s.targetName}`;
    const detailPrefix = s.isPublic ? 'Public synonym' : 'Synonym';
    // Public synonyms demote to tier 4 (other-visible) so the user's own
    // schema's relations win the top of the list. Private synonyms stay
    // at tier 2 alongside the user's tables.
    const tier = s.isPublic ? 4 : 2;
    // Curated data-dictionary views survive the untyped cap; the rest of
    // the synonym flood (schema-derived, thousands on a real DB) stays
    // cappable. See ORACLE_DICTIONARY_RELATION_NAMES.
    const capExempt = ORACLE_DICTIONARY_RELATION_NAMES.has(s.name.toUpperCase());
    out.push({
      label: s.name,
      insertText: formatIdentifier(s.name),
      isSnippet: false,
      kind: 'table',
      tier,
      detail: `${detailPrefix} → ${target}`,
      filterText: s.name,
      ...(capExempt ? { capExempt: true } : {}),
    });
  }
}

/**
 * Surface the two Oracle sequence pseudo-methods (`NEXTVAL` / `CURRVAL`)
 * after a sequence dot. Pinned to tier 1 so they outrank any leak from the
 * surrounding statement.
 */
/**
 * F5.3 Wave-4 → plan19 §4.6 — Detect a sequence name declared earlier in the
 * SAME script via `CREATE [OR REPLACE] SEQUENCE <name>`. Token-scans
 * `[0, boundEnd)` — `boundEnd` is the cursor's own enclosing statement's end
 * (`ctx.statementRange.end`, per `findEnclosingStatement`) — so a mention
 * inside a string/comment (the tokenizer skips both, same idiom as
 * `inBufferTables.ts`'s in-buffer CREATE TABLE scanner) or one that lives in
 * a LATER statement never registers. Retired the former whole-buffer regex,
 * which matched both of those false-positive shapes.
 */
function isBufferLocalOracleSequence(
  source: string,
  boundEnd: number,
  baseFolded: string,
): boolean {
  const tokens = tokenize(source, 0, boundEnd, ENGINE);
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokenTextUpper(tokens[i]) !== 'CREATE') continue;
    let j = i + 1;
    if (tokenTextUpper(tokens[j]) === 'OR' && tokenTextUpper(tokens[j + 1]) === 'REPLACE') {
      j += 2;
    }
    if (tokenTextUpper(tokens[j]) !== 'SEQUENCE') continue;
    const nameTok = tokens[j + 1];
    if (!nameTok || (nameTok.type !== 'ident' && nameTok.type !== 'kw')) continue;
    if (foldIdentifier(nameTok.text) === baseFolded) return true;
  }
  return false;
}

/** Uppercased token text for keyword-shape comparisons; `null` for non-word tokens. */
function tokenTextUpper(tok: Token | undefined): string | null {
  if (!tok || (tok.type !== 'ident' && tok.type !== 'kw')) return null;
  return tok.text.toUpperCase();
}

function emitSequenceMethods(out: CompletionCandidate[]): void {
  for (const m of ORACLE_SEQUENCE_METHODS) {
    out.push({
      label: m,
      insertText: m,
      isSnippet: false,
      kind: 'system-function',
      tier: 1,
      detail: 'Sequence pseudocolumn',
      filterText: m,
    });
  }
}

function emitSequencesAsCandidates(
  out: CompletionCandidate[],
  sequences: readonly OracleSchemaSequence[],
): void {
  for (const s of sequences) {
    out.push({
      label: s.name,
      insertText: formatIdentifier(s.name),
      isSnippet: false,
      kind: 'system-object',
      tier: 3,
      detail: 'Sequence',
      filterText: s.name,
    });
  }
}

function emitPackagesAsCandidates(
  out: CompletionCandidate[],
  packages: readonly OracleSchemaPackage[],
): void {
  for (const p of packages) {
    out.push({
      label: p.name,
      insertText: formatIdentifier(p.name),
      isSnippet: false,
      kind: 'system-object',
      tier: 3,
      detail: `Package (${p.schema})`,
      filterText: p.name,
      // SYS-owned packages reached via a PUBLIC synonym (DBMS_OUTPUT,
      // DBMS_LOB, UTL_FILE, ...) are a bounded population — Oracle's own
      // built-in package catalog — unlike the schema's own packages, which
      // scale with the schema. Flag so the ranker's untyped cap can't slice
      // them on a >500-candidate schema (register #F4 symmetric fix; twin
      // of the dictionary-view synonym exemption in emitSynonymsAsRelations
      // and mssql's sys/INFORMATION_SCHEMA exemption). User packages
      // (isSystemPackage false/absent) stay cappable.
      ...(p.isSystemPackage ? { capExempt: true } : {}),
    });
  }
}

/**
 * F5.3 Wave-4 — Oracle built-in SYS packages. Each entry maps the package
 * name (upper-case) to a curated list of its public members. Coverage is
 * limited to packages exercised by the autocomplete-coverage specs; expand
 * the list as more packages enter coverage.
 */
const ORACLE_BUILTIN_PACKAGE_MEMBERS: Readonly<Record<string, readonly string[]>> = {
  DBMS_OUTPUT: [
    'PUT', 'PUT_LINE', 'NEW_LINE', 'GET_LINE', 'GET_LINES',
    'ENABLE', 'DISABLE',
  ],
  DBMS_MVIEW: [
    'REFRESH', 'REFRESH_ALL_MVIEWS', 'REFRESH_DEPENDENT',
    'EXPLAIN_MVIEW', 'EXPLAIN_REWRITE',
    'PURGE_LOG', 'PURGE_DIRECT_LOAD_LOG', 'PURGE_MVIEW_FROM_LOG',
    'BEGIN_TABLE_REORGANIZATION', 'END_TABLE_REORGANIZATION',
  ],
  DBMS_STATS: [
    'GATHER_TABLE_STATS', 'GATHER_INDEX_STATS', 'GATHER_SCHEMA_STATS',
    'GATHER_DATABASE_STATS', 'DELETE_TABLE_STATS', 'DELETE_INDEX_STATS',
    'DELETE_SCHEMA_STATS', 'EXPORT_TABLE_STATS', 'IMPORT_TABLE_STATS',
    'SET_TABLE_STATS', 'GET_TABLE_STATS',
  ],
  DBMS_LOCK: [
    'SLEEP', 'ALLOCATE_UNIQUE', 'REQUEST', 'CONVERT', 'RELEASE',
  ],
  DBMS_SQL: [
    'OPEN_CURSOR', 'PARSE', 'EXECUTE', 'FETCH_ROWS', 'CLOSE_CURSOR',
    'BIND_VARIABLE', 'DEFINE_COLUMN', 'COLUMN_VALUE',
  ],
  DBMS_SCHEDULER: [
    'CREATE_JOB', 'DROP_JOB', 'ENABLE', 'DISABLE', 'RUN_JOB', 'STOP_JOB',
    'CREATE_PROGRAM', 'DROP_PROGRAM', 'CREATE_SCHEDULE', 'DROP_SCHEDULE',
  ],
  UTL_FILE: [
    'FOPEN', 'FCLOSE', 'FCLOSE_ALL', 'IS_OPEN',
    'PUT', 'PUT_LINE', 'PUTF', 'NEW_LINE',
    'GET_LINE', 'GET_RAW', 'PUT_RAW',
  ],
  UTL_HTTP: [
    'REQUEST', 'REQUEST_PIECES', 'BEGIN_REQUEST', 'END_REQUEST',
    'GET_RESPONSE', 'END_RESPONSE', 'SET_HEADER', 'GET_HEADER',
  ],
};

/**
 * `PKG.<here>` — emit the package's public members (subprograms AND the
 * constants / variables / types / cursors / exceptions the spec declares).
 * Shapes and orders them in `oraclePackageMemberCandidates.ts`; pinned to
 * tier 1 there so no leaking column outranks them.
 */
function emitPackageMembers(
  out: CompletionCandidate[],
  pkg: OracleSchemaPackage,
  args: BuildArgs,
): void {
  out.push(
    ...buildOraclePackageMemberCandidates(pkg, {
      // At a PL/SQL statement position only a PROCEDURE can stand alone, so
      // procedures lead; inside an expression the order stays alphabetical.
      proceduresFirst: isOraclePackageStatementPosition(args.text, args.ctx.offset),
    }),
  );
}

/**
 * Emit `LEVEL` / `CONNECT_BY_ISLEAF` / `CONNECT_BY_ROOT` /
 * `SYS_CONNECT_BY_PATH` at tier 1 when the cursor's statement contains
 * `CONNECT BY` or `START WITH`. The four are Oracle hierarchical helpers
 * that only make sense inside a hierarchical query — gating on
 * statement-scope keeps them off ordinary SELECTs and prevents pollution.
 */
function emitHierarchicalCandidatesIfActive(
  out: CompletionCandidate[],
  args: BuildArgs,
): void {
  const { ctx, text } = args;
  if (!isInConnectByContext(text, ctx.statementRange.start, ctx.statementRange.end)) {
    return;
  }
  for (const c of getOracleHierarchicalCandidates()) {
    out.push({
      label: c.label,
      insertText: c.insertText,
      isSnippet: c.isSnippet,
      kind: 'system-function',
      tier: 1,
      detail: c.detail,
      filterText: c.label,
      ...(c.triggerSignatureHelp ? { triggerSignatureHelp: true } : {}),
    });
  }
}

function emitCteCandidates(out: CompletionCandidate[], ctx: BuildArgs['ctx']): void {
  for (const cte of ctx.cteBindings) {
    out.push({
      label: cte.name,
      insertText: cte.name,
      isSnippet: false,
      kind: 'cte',
      tier: 5,
      detail: 'CTE',
      filterText: cte.name,
    });
  }
}

/**
 * F5.3 R-bucket — Oracle data-dictionary catalog views. The snapshot doesn't
 * carry these (they're synthetic database state), so `emitAliasColumns` and
 * the dotted-schema emitter fall through to this map for catalog-view alias
 * binds (`FROM user_tables ut` → `ut.|`) and bare catalog references
 * (`SELECT … FROM user_tables`). Coverage is the columns exercised by the
 * autocomplete-coverage specs (`Docs/AutocompleteTestScripts/oracle.md`
 * scripts 24/etc.); expand as more catalog views enter coverage. Keys
 * are folded to UPPERCASE to match Oracle's data-dictionary convention.
 */
const ORACLE_SYSTEM_TABLE_COLUMNS: Readonly<Record<string, readonly string[]>> = {
  USER_TABLES: [
    'TABLE_NAME', 'TABLESPACE_NAME', 'CLUSTER_NAME', 'IOT_NAME', 'STATUS',
    'PCT_FREE', 'PCT_USED', 'INI_TRANS', 'MAX_TRANS', 'INITIAL_EXTENT',
    'NEXT_EXTENT', 'MIN_EXTENTS', 'MAX_EXTENTS', 'LOGGING', 'BACKED_UP',
    'NUM_ROWS', 'BLOCKS', 'EMPTY_BLOCKS', 'AVG_SPACE', 'CHAIN_CNT',
    'AVG_ROW_LEN', 'NUM_FREELIST_BLOCKS', 'DEGREE', 'INSTANCES', 'CACHE',
    'TABLE_LOCK', 'SAMPLE_SIZE', 'LAST_ANALYZED', 'PARTITIONED',
    'IOT_TYPE', 'TEMPORARY', 'SECONDARY', 'NESTED', 'BUFFER_POOL',
    'FLASH_CACHE', 'CELL_FLASH_CACHE', 'ROW_MOVEMENT', 'GLOBAL_STATS',
    'USER_STATS', 'DURATION', 'SKIP_CORRUPT', 'MONITORING', 'CLUSTER_OWNER',
    'DEPENDENCIES', 'COMPRESSION', 'COMPRESS_FOR', 'DROPPED',
    'READ_ONLY', 'SEGMENT_CREATED', 'RESULT_CACHE',
  ],
  ALL_TABLES: [
    'OWNER', 'TABLE_NAME', 'TABLESPACE_NAME', 'CLUSTER_NAME', 'IOT_NAME',
    'STATUS', 'PCT_FREE', 'NUM_ROWS', 'BLOCKS', 'AVG_ROW_LEN',
    'LAST_ANALYZED', 'PARTITIONED', 'TEMPORARY', 'LOGGING', 'COMPRESSION',
    'COMPRESS_FOR', 'READ_ONLY', 'SEGMENT_CREATED', 'NESTED', 'IOT_TYPE',
  ],
  DBA_TABLES: [
    'OWNER', 'TABLE_NAME', 'TABLESPACE_NAME', 'CLUSTER_NAME', 'IOT_NAME',
    'STATUS', 'PCT_FREE', 'NUM_ROWS', 'BLOCKS', 'AVG_ROW_LEN',
    'LAST_ANALYZED', 'PARTITIONED', 'TEMPORARY', 'LOGGING', 'COMPRESSION',
    'COMPRESS_FOR', 'READ_ONLY', 'SEGMENT_CREATED',
  ],
  USER_TAB_COLUMNS: [
    'TABLE_NAME', 'COLUMN_NAME', 'DATA_TYPE', 'DATA_TYPE_MOD', 'DATA_TYPE_OWNER',
    'DATA_LENGTH', 'DATA_PRECISION', 'DATA_SCALE', 'NULLABLE', 'COLUMN_ID',
    'DEFAULT_LENGTH', 'DATA_DEFAULT', 'NUM_DISTINCT', 'LOW_VALUE',
    'HIGH_VALUE', 'DENSITY', 'NUM_NULLS', 'NUM_BUCKETS', 'LAST_ANALYZED',
    'SAMPLE_SIZE', 'CHARACTER_SET_NAME', 'CHAR_COL_DECL_LENGTH', 'GLOBAL_STATS',
    'USER_STATS', 'AVG_COL_LEN', 'CHAR_LENGTH', 'CHAR_USED', 'V80_FMT_IMAGE',
    'DATA_UPGRADED', 'HISTOGRAM', 'DEFAULT_ON_NULL', 'IDENTITY_COLUMN',
    'EVALUATION_EDITION', 'UNUSABLE_BEFORE', 'UNUSABLE_BEGINNING',
    'COLLATION', 'COLLATED_COLUMN_ID',
  ],
  ALL_TAB_COLUMNS: [
    'OWNER', 'TABLE_NAME', 'COLUMN_NAME', 'DATA_TYPE', 'DATA_LENGTH',
    'DATA_PRECISION', 'DATA_SCALE', 'NULLABLE', 'COLUMN_ID', 'DEFAULT_LENGTH',
    'DATA_DEFAULT', 'NUM_DISTINCT', 'LAST_ANALYZED', 'CHARACTER_SET_NAME',
    'HISTOGRAM', 'DEFAULT_ON_NULL', 'IDENTITY_COLUMN',
  ],
  DBA_TAB_COLUMNS: [
    'OWNER', 'TABLE_NAME', 'COLUMN_NAME', 'DATA_TYPE', 'DATA_LENGTH',
    'DATA_PRECISION', 'DATA_SCALE', 'NULLABLE', 'COLUMN_ID',
  ],
  USER_OBJECTS: [
    'OBJECT_NAME', 'SUBOBJECT_NAME', 'OBJECT_ID', 'DATA_OBJECT_ID',
    'OBJECT_TYPE', 'CREATED', 'LAST_DDL_TIME', 'TIMESTAMP', 'STATUS',
    'TEMPORARY', 'GENERATED', 'SECONDARY', 'NAMESPACE', 'EDITION_NAME',
  ],
  USER_INDEXES: [
    'INDEX_NAME', 'INDEX_TYPE', 'TABLE_OWNER', 'TABLE_NAME', 'TABLE_TYPE',
    'UNIQUENESS', 'COMPRESSION', 'PREFIX_LENGTH', 'TABLESPACE_NAME',
    'INI_TRANS', 'MAX_TRANS', 'INITIAL_EXTENT', 'NEXT_EXTENT', 'MIN_EXTENTS',
    'MAX_EXTENTS', 'PCT_INCREASE', 'PCT_THRESHOLD', 'STATUS', 'NUM_ROWS',
    'BLEVEL', 'LEAF_BLOCKS', 'DISTINCT_KEYS', 'LAST_ANALYZED', 'PARTITIONED',
    'TEMPORARY', 'GENERATED', 'SECONDARY', 'VISIBILITY',
  ],
  USER_CONSTRAINTS: [
    'OWNER', 'CONSTRAINT_NAME', 'CONSTRAINT_TYPE', 'TABLE_NAME',
    'SEARCH_CONDITION', 'R_OWNER', 'R_CONSTRAINT_NAME', 'DELETE_RULE',
    'STATUS', 'DEFERRABLE', 'DEFERRED', 'VALIDATED', 'GENERATED',
    'BAD', 'RELY', 'LAST_CHANGE', 'INDEX_OWNER', 'INDEX_NAME', 'INVALID',
  ],
  USER_VIEWS: [
    'VIEW_NAME', 'TEXT_LENGTH', 'TEXT', 'TYPE_TEXT_LENGTH', 'TYPE_TEXT',
    'OID_TEXT_LENGTH', 'OID_TEXT', 'VIEW_TYPE_OWNER', 'VIEW_TYPE',
    'SUPERVIEW_NAME', 'EDITIONING_VIEW', 'READ_ONLY',
  ],
  USER_SYNONYMS: [
    'SYNONYM_NAME', 'TABLE_OWNER', 'TABLE_NAME', 'DB_LINK',
  ],
  USER_SEQUENCES: [
    'SEQUENCE_NAME', 'MIN_VALUE', 'MAX_VALUE', 'INCREMENT_BY',
    'CYCLE_FLAG', 'ORDER_FLAG', 'CACHE_SIZE', 'LAST_NUMBER',
  ],
  USER_TRIGGERS: [
    'TRIGGER_NAME', 'TRIGGER_TYPE', 'TRIGGERING_EVENT', 'TABLE_OWNER',
    'BASE_OBJECT_TYPE', 'TABLE_NAME', 'COLUMN_NAME', 'REFERENCING_NAMES',
    'WHEN_CLAUSE', 'STATUS', 'DESCRIPTION', 'ACTION_TYPE', 'TRIGGER_BODY',
    'CROSSEDITION', 'BEFORE_STATEMENT', 'BEFORE_ROW', 'AFTER_ROW',
    'AFTER_STATEMENT', 'INSTEAD_OF_ROW', 'FIRE_ONCE', 'APPLY_SERVER_ONLY',
  ],
};

/**
 * The curated Oracle data-dictionary relation names (Plan 15 Gate D round 2,
 * script.24 §2). These reach the FROM position as PUBLIC synonyms — `kind:
 * 'table'` inside the multi-thousand SYS-internal synonym flood a real
 * Oracle DB exposes (`_ALL_QUEUE_CACHED_MESSAGES`, `ALL_APPLY_*`, …) — so
 * without a per-candidate exemption the ranker's untyped cap sliced exactly
 * the well-known `USER_*` / `ALL_*` / `DBA_*` views a user actually types
 * (`missing=[user_tables, …, dba_tables]` while the flood survived). Bounded
 * by construction: the union of the dotted-access catalog registry's keys
 * and the curated `ORACLE_DATA_DICTIONARY_VIEWS` vocabulary (~60 names).
 * Consumed by `emitSynonymsAsRelations` to mark matching synonyms
 * `capExempt` for the ranker's untyped cap (see `rank.ts`).
 */
const ORACLE_DICTIONARY_RELATION_NAMES: ReadonlySet<string> = new Set([
  ...ORACLE_DATA_DICTIONARY_VIEWS,
  ...Object.keys(ORACLE_SYSTEM_TABLE_COLUMNS),
]);

// CB1.7 — Continuation keywords that should rank tier 1 at the
// "select-list, after a completed projection" sub-position. Oracle uses
// MINUS instead of EXCEPT for set difference. Intersect with the curated
// keyword set so we only promote labels that are actually emitted.
const ORACLE_NEXT_AFTER_SELECT_PROJECTION: ReadonlySet<string> = new Set(
  ['FROM', 'UNION', 'INTERSECT', 'MINUS', 'FOR'].filter((k) =>
    ORACLE_KEYWORD_SET.has(k),
  ),
);

// PL/SQL declaration-section + body statement-starter keywords. At a procedural
// statement-start (a fresh DECLARE-section item or a body statement after `;`,
// `BEGIN`, `THEN`, `ELSE`, `LOOP`) these are the most likely next tokens — a
// schema object is not legal as the first token of a PL/SQL statement — so they
// are promoted to tier 1 above the buffer-referenced tables that would
// otherwise bury them below Monaco's virtualized fold.
const ORACLE_PLSQL_STATEMENT_STARTERS: ReadonlySet<string> = new Set(
  [
    // DECLARE-section item starters
    'TYPE', 'SUBTYPE', 'CURSOR', 'PROCEDURE', 'FUNCTION', 'PRAGMA',
    // body statement starters
    'BEGIN', 'DECLARE', 'IF', 'CASE', 'LOOP', 'WHILE', 'FOR', 'FORALL',
    'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'MERGE', 'RETURN', 'NULL',
    'OPEN', 'FETCH', 'CLOSE', 'EXIT', 'CONTINUE', 'GOTO', 'RAISE',
    'COMMIT', 'ROLLBACK', 'SAVEPOINT', 'EXECUTE',
  ].filter((k) => ORACLE_KEYWORD_SET.has(k)),
);

// CB1.7 — Continuation keywords that should rank tier 1 at the
// "from, after a completed relation" sub-position. Includes Oracle-specific
// hierarchical-query continuations (`START WITH …`, `CONNECT BY …`).
const ORACLE_NEXT_AFTER_FROM_RELATION: ReadonlySet<string> = new Set(
  [
    'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS',
    'ON', 'USING', 'AS', 'ORDER', 'GROUP', 'HAVING', 'UNION', 'INTERSECT',
    'MINUS', 'FOR', 'START', 'CONNECT',
  ].filter((k) => ORACLE_KEYWORD_SET.has(k)),
);

/**
 * Emit the curated Oracle data type list at a "data-type" slot. Tier 1 so
 * types outrank tables/columns/keywords. Multi-token entries like
 * `INTERVAL DAY TO SECOND` are emitted as-is. User-defined types
 * (`ADDRESS_TYPE` from `ALL_TYPES`) are emitted first — they're the ones the
 * user actually declared — verbatim (Oracle-uppercase) so the inserted text is
 * a valid type reference.
 */
function emitOracleDataTypeCandidates(
  out: CompletionCandidate[],
  userTypes: readonly string[] = [],
): void {
  const seen = new Set<string>();
  for (const name of userTypes) {
    const upper = name.toUpperCase();
    if (seen.has(upper)) continue;
    seen.add(upper);
    out.push({
      label: name,
      insertText: name,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'user-defined type',
    });
  }
  for (const type of getOracleDataTypes()) {
    out.push({
      label: type,
      insertText: type,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'Oracle data type',
    });
  }
}

function emitOraclePlsqlTypePatternCandidates(out: CompletionCandidate[]): void {
  for (const pat of getOraclePlsqlTypePatterns()) {
    out.push({
      label: pat,
      insertText: pat,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'PL/SQL type-reference pattern',
    });
  }
}

// Oracle ORDER BY direction + null-ordering keywords. ASC/DESC are
// single-token (live in the curated keyword list); NULLS FIRST/LAST are
// two-token clauses the curated emitter cannot express, so the resolver
// pushes them directly when `positionKind === 'order-by'`.
const ORACLE_ORDER_BY_DIRECTION_KEYWORDS: readonly string[] = [
  'ASC',
  'DESC',
  'NULLS FIRST',
  'NULLS LAST',
];

/**
 * F5.3 Wave-4 R-bucket — emit Oracle's 22 predefined PL/SQL exception names
 * (`NO_DATA_FOUND`, `TOO_MANY_ROWS`, `OTHERS`, etc.) at the exception-handler
 * `WHEN ` slot. Pinned to tier 1 so they outrank the procedural-locals list
 * (which leaks into the `unknown` position kind via `emitOracleProceduralLocalCandidates`).
 * `kind: 'keyword'` keeps them aligned with the orchestrator's keyword
 * casing post-process when the user prefers a non-UPPER style, though the
 * canonical Oracle form is UPPER.
 */
function emitPlsqlExceptionNames(out: CompletionCandidate[]): void {
  for (const name of ORACLE_PLSQL_EXCEPTION_NAMES) {
    out.push({
      label: name,
      insertText: name,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'PL/SQL predefined exception',
      filterText: name,
    });
  }
}

// Emit ONLY the grammatically-legal continuation keywords for a recognized
// sub-position, ordered by popularity (orderHint = index). Returns true when
// it emitted (caller should then return — these are keyword-only boundaries).
function emitLegalNextKeywords(
  out: CompletionCandidate[],
  slot: NextKeywordSlotResult,
  // Shifts the popularity order when a leading candidate (the generated
  // table alias) already occupies orderHint 0.
  orderHintBase = 0,
): boolean {
  const kws = getLegalNextKeywords(ENGINE, slot.slot, {
    relationHasAlias: slot.relationHasAlias,
    projectionIsStar: slot.projectionIsStar,
  });
  if (kws.length === 0) return false;
  // The slot IS answered whenever it has a vocabulary, so `true` (the caller's
  // short-circuit) does not depend on how many items are new — a keyword an
  // earlier, more specific emission already offered (a compound remainder at
  // `NULLS F▮`) is skipped rather than repeated in the widget.
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  kws.forEach((kw, i) => {
    if (alreadyOffered.has(kw.toUpperCase())) return;
    out.push({ label: kw, insertText: kw, isSnippet: false, kind: 'keyword', tier: 1, detail: 'SQL keyword', orderHint: orderHintBase + i });
  });
  return true;
}

function emitKeywords(
  out: CompletionCandidate[],
  nextLikely: ReadonlySet<string> = new Set(),
): void {
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  for (const kw of getOracleKeywordsForPosition()) {
    // A more specific emission already offered this keyword (e.g. a compound
    // remainder at tier 1) — don't repeat it in the widget.
    if (alreadyOffered.has(kw)) continue;
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: nextLikely.has(kw) ? 1 : 7,
      detail: 'SQL keyword',
    });
  }
}

/**
 * F5.3 Wave-4 — extract the indexed table from the `ON [<schema>.]<tbl>(`
 * segment of a `CREATE INDEX ... ON <tbl> (` Oracle statement. Returns
 * `{ schema, name }` folded as Oracle reads them — a bare part upper-cased, a
 * delimited one unquoted with its case kept, so `"orders"` still names
 * `"orders"` and not its sibling `ORDERS` — or null when the segment doesn't
 * match. Same for the REFERENCES / ALTER TABLE readers below.
 */
function findOracleIndexTargetTable(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { schema: string | null; name: string } | null {
  const slice = text.slice(statementStart, cursorOffset);
  const m = slice.match(
    /\bON\s+(?:("(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*\.\s*)?("(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*\(/i,
  );
  if (!m || !m[2]) return null;
  const schemaRaw = m[1] ?? null;
  const nameRaw = m[2]!;
  return {
    schema: schemaRaw ? foldIdentifier(schemaRaw) : null,
    name: foldIdentifier(nameRaw),
  };
}

/**
 * F5.3 Wave-4 — extract the REFERENCED table from the LAST
 * `REFERENCES [<schema>.]<tbl>(` segment before the cursor (multi-FK
 * statements pick the last match).
 */
function findOracleReferencesTargetTable(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { schema: string | null; name: string } | null {
  const slice = text.slice(statementStart, cursorOffset);
  const re = /\bREFERENCES\s+(?:("(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*\.\s*)?("(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*\(/gi;
  let m: RegExpExecArray | null = null;
  let last: RegExpExecArray | null = null;
  while ((m = re.exec(slice)) !== null) last = m;
  if (!last || !last[2]) return null;
  const schemaRaw = last[1] ?? null;
  const nameRaw = last[2]!;
  return {
    schema: schemaRaw ? foldIdentifier(schemaRaw) : null,
    name: foldIdentifier(nameRaw),
  };
}

/**
 * F5.3 Wave-4 — extract the ALTER TABLE target from `ALTER TABLE [<schema>.]<tbl>`
 * in the cursor's statement preamble. Used by the
 * `ddl-alter-table-constraint-column-list` slot. Oracle folds identifiers
 * uppercase by default.
 */
function findOracleAlterTableTarget(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { schema: string | null; name: string } | null {
  const slice = text.slice(statementStart, cursorOffset);
  const m = slice.match(
    // Trailing `(?![\w$])` (not `\b`): a closing `"` is a non-word char, so
    // `\b` never matched after a delimited target followed by whitespace.
    /\bALTER\s+TABLE\s+(?:("(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*\.\s*)?("(?:[^"]|"")*"|[A-Za-z_][\w$]*)(?![\w$])/i,
  );
  if (!m || !m[2]) return null;
  const schemaRaw = m[1] ?? null;
  const nameRaw = m[2]!;
  return {
    schema: schemaRaw ? foldIdentifier(schemaRaw) : null,
    name: foldIdentifier(nameRaw),
  };
}

/**
 * F5.3 Wave-4 — emit columns of a specific target table at tier 1. Used by
 * `ddl-index-column` and `ddl-references-column-list`. Oracle's snapshot is
 * per-schema, so we narrow on (schema, name) when schema is explicit.
 */
function emitOracleTargetTableColumns(
  out: CompletionCandidate[],
  target: { schema: string | null; name: string },
  tables: ReadonlyArray<SnapshotTable>,
  opts?: { pkFirst?: boolean },
): void {
  // The table Oracle resolves the target to: its exact spelling first, so a
  // bare `orders` (ORDERS) and a quoted `"orders"` each name their own table.
  const tbl = resolveRelation(tables, target.name, target.schema);
  if (!tbl) return;
  for (const c of tbl.columns) {
    // pkFirst (REFERENCES / constraint column-lists): the referenced table's
    // PK is what an FK points at in almost every case — keep it at tier 1
    // and drop the rest to tier 2 (mirrors the MSSQL resolver).
    const pk = c.isPrimaryKey === true;
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: opts?.pkFirst && !pk ? 2 : 1,
      detail: opts?.pkFirst && pk ? `${c.dataType} (PK, ${tbl.name})` : `${c.dataType} (${tbl.name})`,
    });
  }
}

/**
 * JCS Slice C3 — type-aware Oracle JSON operator emission. Walks back from
 * the cursor to find the preceding column operand; for JSON-classified
 * columns (`native-json` from Oracle 21c+ or `check-isjson` from `IS JSON`
 * constraints) surfaces `JSON_EXISTS`, `JSON_VALUE`, `JSON_QUERY`,
 * `IS [NOT] JSON`, plus dot-notation continuation via SR-RO's
 * `suggestOperatorsForOperand`.
 */
function emitTypeAwareJsonOperators(out: CompletionCandidate[], args: BuildArgs): void {
  const operand = findPrecedingColumnOperand({
    text: args.text,
    offset: args.ctx.offset,
    statementStart: args.ctx.statementRange.start,
    aliasBindings: args.ctx.aliasBindings,
    snapshotTables: args.snapshotTables,
    fold: foldIdentifier,
  });
  if (operand) {
    emitTypeAwareOperators(out, operand.jsonClassification, operand.column.dataType, 'oracle', args.ctx.positionKind);
    return;
  }
  // Slice 3a — typed expression operand (`CAST(x AS NUMBER) ▮`, `UPPER(c) ▮`).
  const exprType = findPrecedingExpressionType({
    text: args.text,
    offset: args.ctx.offset,
    statementStart: args.ctx.statementRange.start,
    engine: 'oracle',
    resolveRoutineReturnType: makeRoutineReturnResolver(args.snapshotRoutines, (s) => s.toLowerCase()),
  });
  if (exprType) emitTypeAwareExpressionOperators(out, exprType, 'oracle', args.ctx.positionKind);
}

/**
 * JCS Slice C4 — JSON path completion for Oracle. Mirrors PG's
 * `collectPgJsonPathCandidates`. Detects whether the cursor sits inside a
 * recognised JSON-path string (after `JSON_VALUE(col, `, `JSON_QUERY(col, `,
 * `JSON_EXISTS(col, `), resolves the LHS column reference against the snapshot
 * honouring FROM/JOIN aliases via the shared resolveColumnRefToTable (bindings
 * come from ctx.aliasBindings, a token-stream scan independent of the
 * tree-sitter tree), using Oracle's uppercase-default fold, and emits sampled path hints
 * from the per-session cache when the column is JSON-classified.
 */
function collectOracleJsonPathCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as Oracle resolves it. */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  const pos = findJsonPathStringPosition(args.text, args.offset);
  if (!pos) return [];
  const colSegment = readOracleIdentifierBefore(args.text, pos.columnSideEnd);
  if (!colSegment) return [];
  const columnRef = colSegment.parts.join('.');
  const resolved = resolveColumnRefToTable(
    columnRef,
    args.aliasBindings,
    args.snapshotTables,
    foldIdentifier,
    args.resolveTable,
  );
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;
  // Trust the snapshot generator to populate `tbl.schema` — matches PG sibling.
  const hintSchema = tbl.schema ?? '';
  // Offer JSON-path hints when the bus classified the column JSON
  // (engine-native / CHECK) OR the user declared it via MetaInfo
  // ("Treat as JSON" — a plain string column the schema bus can't know
  // about). The declaration source is invalidated by the Table Designer
  // toggle, so this reflects changes without a reconnect.
  const busJson =
    !!colMeta.jsonClassification &&
    colMeta.jsonClassification.kind !== 'not-json';
  if (
    !busJson &&
    !isJsonColumnDeclared(
      args.connectionId, args.database, hintSchema, tbl.name, colMeta.name,
    )
  ) {
    return [];
  }
  const cacheEntry = getPathHints(args.connectionId, args.database, {
    engine: 'oracle',
    schema: hintSchema,
    table: tbl.name,
    column: colMeta.name,
  });
  const declaredHints = getJsonPathHints(
    args.connectionId,
    args.database,
    hintSchema,
    tbl.name,
    colMeta.name,
  );
  const out: CompletionCandidate[] = [];
  emitJsonPathCandidates(out, {
    cacheEntry,
    declaredHints,
    positionKind: pos.kind,
    partial: pos.partial,
    engine: 'oracle',
  });
  return out;
}

/**
 * Phase 3 — enum-value completion for Oracle. Resolves the LHS column
 * reference against the snapshot honouring FROM/JOIN aliases via the shared
 * resolveColumnRefToTable (bindings come from ctx.aliasBindings, a
 * token-stream scan independent of the tree-sitter tree), using Oracle's
 * uppercase-default fold.
 *
 * Path-aware extension (MERGE-2): when the LHS of the comparison is an
 * Oracle `JSON_VALUE(col, '<path>')` expression, the resolved canonical
 * path is passed to `emitEnumValueCandidates` so the path-keyed MetaInfo
 * enum cache is consulted instead of the column-level cache. Wildcard
 * probe is handled inside `emitEnumValueCandidates`.
 */
function collectOracleEnumValueCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as Oracle resolves it. */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  // Table DDL carries no queried relation, so a bare column reference here
  // must not fall back to an unrelated snapshot table's cached values
  // (see `isInsideTableDdlStatement`). Closes oracle script.29 §4.
  if (isInsideTableDdlStatement(args.text, args.offset)) return [];
  const cmp = detectComparisonValueContext(args.text, args.offset);
  if (!cmp) {
    // detectComparisonValueContext misses JSON-chain LHS (JSON_VALUE(col, '…') = …)
    // because the char immediately before `=` is the closing `)` of the function
    // call, not part of an identifier. Try the JSON-path extractor directly against
    // each candidate column; the extractor's own findComparisonOpEndBefore gate
    // keeps this cheap — it returns null immediately when the cursor isn't at a
    // comparison RHS.
    return collectOracleJsonChainEnumValueCandidates(args);
  }
  const resolved = resolveColumnRefToTable(
    cmp.columnRef,
    args.aliasBindings,
    args.snapshotTables,
    foldIdentifier,
    args.resolveTable,
  );
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;
  // Trust the snapshot generator to populate `tbl.schema` — matches PG sibling.
  const hintSchema = tbl.schema ?? '';

  // Try to extract a JSON path from the cursor context (Oracle-specific).
  // `extractJsonPathFromCursorOracle` looks at the LHS for `JSON_VALUE(...)`
  // expressions and returns the canonical path.
  const pathResult = extractJsonPathFromCursorOracle({
    text: args.text,
    cursorOffset: args.offset,
    column: colMeta.name,
  });

  const out: CompletionCandidate[] = [];
  emitEnumValueCandidates(out, {
    connectionId: args.connectionId,
    database: args.database,
    schema: hintSchema,
    table: tbl.name,
    column: colMeta.name,
    // Pass the canonical path when a JSON_VALUE expression was detected.
    path: pathResult?.canonicalPath,
    quote: findValueStringBodyStart(args.text, args.offset) === undefined,
  });
  return out;
}

/**
 * Fallback for JSON-chain LHS comparisons that `detectComparisonValueContext`
 * misses (e.g. `JSON_VALUE(col, '$.key') = '|'`). Iterates over every column
 * visible via the snapshot tables, runs the Oracle JSON-path extractor for
 * each, and emits enum-value candidates for the first match.
 *
 * The extractor's own `findComparisonOpEndBefore` gate makes this cheap: it
 * returns `null` immediately when the cursor is not at a comparison RHS, so
 * the loop exits fast in non-matching positions.
 */
function collectOracleJsonChainEnumValueCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as Oracle resolves it. */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  const quote = findValueStringBodyStart(args.text, args.offset) === undefined;

  // The extractor's backward scan never depends on the column (it is only
  // compared at the end) — scan once, then match the column it names. This
  // fallback runs on nearly every request; one scan per snapshot column was
  // ~20% of provider time on a 600-table schema (1.5.1 F2).
  const pathResult = extractJsonPathFromCursorOracle({ text: args.text, cursorOffset: args.offset });
  if (!pathResult) return [];
  const targetColumn = pathResult.column.toLowerCase();
  // The statement's own relations (as Oracle resolves them) first, then the
  // first snapshot table owning the column.
  for (const tbl of boundTablesFirst(args.aliasBindings, args.snapshotTables, args.resolveTable)) {
    // Trust the snapshot generator to populate `tbl.schema` — matches PG sibling.
    const hintSchema = tbl.schema ?? '';
    for (const col of tbl.columns) {
      if (col.name.toLowerCase() !== targetColumn) continue;

      // Found a JSON-chain match. Emit enum values for this column + path.
      const out: CompletionCandidate[] = [];
      emitEnumValueCandidates(out, {
        connectionId: args.connectionId,
        database: args.database,
        schema: hintSchema,
        table: tbl.name,
        column: col.name,
        path: pathResult.canonicalPath,
        quote,
      });
      if (out.length > 0) return out;
    }
  }
  return [];
}

/**
 * Read a 1–3-part dotted identifier ending at `offset`. Accepts bare and
 * double-quoted segments — Oracle's two input forms.
 */
function readOracleIdentifierBefore(
  text: string,
  offset: number,
): { readonly parts: readonly string[] } | null {
  let i = offset;
  while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  const parts: string[] = [];
  while (i > 0) {
    const end = i;
    while (i > 0 && /[A-Za-z0-9_"$]/.test(text[i - 1]!)) i -= 1;
    if (i === end) break;
    parts.unshift(text.slice(i, end));
    while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
    if (text[i - 1] !== '.') break;
    i -= 1;
    while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  }
  return parts.length > 0 ? { parts } : null;
}

function emitBuiltinFunctions(out: CompletionCandidate[]): void {
  for (const fn of getOracleBuiltinFunctions()) {
    out.push({
      label: fn,
      insertText: `${fn}($0)`,
      isSnippet: true,
      kind: 'system-function',
      tier: 8,
      detail: 'Oracle function',
      filterText: fn,
      triggerSignatureHelp: true,
    });
  }
}

function emitRoutines(
  out: CompletionCandidate[],
  routines: readonly SnapshotRoutine[],
  currentSchema: string | undefined,
): void {
  const schemaFolded = currentSchema ? currentSchema.toUpperCase() : null;
  for (const r of routines) {
    const isCurrent =
      !r.schema || (schemaFolded && r.schema.toUpperCase() === schemaFolded);
    out.push({
      label: r.name,
      insertText: `${r.name}($0)`,
      isSnippet: true,
      kind: 'routine',
      tier: isCurrent ? 3 : 4,
      detail: r.kind === 'procedure' ? 'Procedure' : 'Function',
      filterText: r.name,
      triggerSignatureHelp: true,
    });
  }
}

/**
 * FK-driven JOIN snippets. Edges match by relation identity
 * (`fkEndpointMatch.ts`): a quoted `"orders"` and `ORDERS` are two tables, so
 * each takes only the FKs whose endpoint names it exactly — and the dedupe
 * keeps both spellings as distinct candidates.
 */
function emitFkJoinSnippets(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: ReadonlyArray<SnapshotTable>,
  fks: readonly FkEdge[],
): void {
  const existingAliases = new Set<string>(bindings.map((b) => b.alias.toLowerCase()));
  const seen = new Set<string>();
  const match = makeFkEndpointMatcher(tables, oracleBindingResolver(tables));
  // One index per pass — never cached across requests (`fkEdgeIndex.ts`).
  const applicableFks = makeApplicableFksFinder(fks, match);
  for (const binding of bindings) {
    if (binding.kind === 'cte' || binding.kind === 'subquery' || binding.kind === 'derived') {
      continue;
    }
    for (const t of tables) {
      const candidate: TableMeta = {
        name: t.name,
        schema: t.schema,
        database: t.database,
        kind: (t.kind === 'view' ? 'view' : 'table') as TableMeta['kind'],
        columns: [],
      };
      const applicable = applicableFks(binding, candidate);
      for (const fk of applicable) {
        const dedupeKey = `${binding.alias.toLowerCase()}|${t.schema ?? ''}.${t.name}|${fk.name ?? `${fk.fromTable}->${fk.toTable}`}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        out.push(
          generateFkJoinSnippet({
            from: binding,
            candidate,
            fk,
            existingAliases,
            engine: ENGINE,
            match,
          }),
        );
      }
    }
  }
}

/**
 * Oracle's half of the shared MERGE-VALUES qualifier policy
 * (`resolver/mergeValuesQualified.ts`): recognise the slot, discover each
 * binding's column names through this resolver's own alias-column emitter,
 * and hand both to the shared rule.
 */
function emitOracleMergeValuesColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): void {
  if (
    !isAtMergeValuesElement(
      args.text,
      ctx.statementRange.start,
      ctx.statementRange.end,
      ctx.offset,
      ENGINE,
    )
  ) {
    return;
  }
  const { target, sources } = partitionMergeBindings(ctx.aliasBindings);
  if (sources.length === 0) return;
  const columnNamesOf = (binding: AliasBinding): readonly string[] => {
    const scratch: CompletionCandidate[] = [];
    emitAliasColumns(
      scratch, binding, args.snapshotTables, ctx.cteBindings, args.synonyms, args.text,
    );
    return scratch.map((c) => c.label);
  };
  emitMergeValuesQualifiedColumns({
    out,
    sources: sources.map((binding) => ({ binding, columns: columnNamesOf(binding) })),
    targetColumns: target ? columnNamesOf(target) : [],
    formatColumn: formatIdentifier,
  });
}

function emitAllBoundAliasColumns(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: ReadonlyArray<SnapshotTable>,
  cteBindings: readonly CteBinding[],
  synonyms: readonly OracleSchemaSynonym[],
  text: string,
): void {
  for (const b of bindings) {
    emitAliasColumns(out, b, tables, cteBindings, synonyms, text);
  }
}

/**
 * F5.3-R-bucket — emit each bound alias's NAME as a tier-1 `alias`-kind
 * candidate. Used at the bare `ON (…)` position inside JOIN / MERGE so the
 * suggest widget shows `tgt`, `src`, … alongside their expanded columns —
 * matches the Script-49 §1 expectation that the bare ON position offers
 * both the alias labels and their column sets.
 *
 * `detail` follows the existing alias-binding pretty-print: the bound
 * relation name (or `<derived>` / `<subquery>` for non-table bindings).
 */
function emitAliasNames(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: ReadonlyArray<SnapshotTable>,
): void {
  const seen = new Set<string>();
  for (const b of bindings) {
    if (seen.has(b.alias)) continue;
    // A synthetic alias for an unaliased pivoted inline view is internal —
    // the user cannot type it, so never offer it as an alias candidate (its
    // columns are still expanded elsewhere).
    if (b.alias === PIVOT_SYNTHETIC_ALIAS) continue;
    seen.add(b.alias);
    let detail: string;
    if (b.kind === 'subquery' || b.kind === 'derived') {
      detail = `<${b.kind}>`;
    } else {
      const relationFolded = foldIdentifier(b.relation);
      const tbl = tables.find((t) => t.name.toUpperCase() === relationFolded);
      detail = tbl ? tbl.name : b.relation;
    }
    out.push({
      label: b.alias,
      insertText: formatIdentifier(b.alias),
      isSnippet: false,
      kind: 'alias',
      tier: 1,
      detail,
    });
  }
}

/**
 * Emit inferred subquery/derived column names as tier-1 column candidates.
 * Oracle stores unquoted identifiers UPPER-folded in the data dictionary;
 * `inferSubqueryColumns` returns the source-text content after quote
 * stripping, so we canonicalise bareword shapes (`employee_id` →
 * `EMPLOYEE_ID`) to match the engine's stored form and let the PK-rank
 * heuristic identify columns case-insensitively. Quoted identifiers (spaces /
 * quote-only chars) are preserved verbatim. Shared by the alias-dot path and
 * the PIVOT-interior path.
 */
function pushOracleSubqueryColumns(
  out: CompletionCandidate[],
  names: readonly string[],
  aliasLabel: string,
): void {
  // An unaliased pivoted inline view carries the internal synthetic alias —
  // show a friendly `(pivot)` detail instead of leaking `__pivot`.
  const detailLabel = aliasLabel === PIVOT_SYNTHETIC_ALIAS ? 'pivot' : aliasLabel;
  for (const name of names) {
    const canonical = canonicalizeInferredOracleName(name);
    out.push({
      label: canonical,
      insertText: canonical,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `(${detailLabel})`,
    });
  }
}

function emitAliasColumns(
  out: CompletionCandidate[],
  binding: AliasBinding,
  tables: ReadonlyArray<SnapshotTable>,
  cteBindings: readonly CteBinding[],
  synonyms: readonly OracleSchemaSynonym[],
  text: string = '',
): void {
  if (binding.kind === 'subquery' || binding.kind === 'derived') {
    // Writer-declared COLUMNS clause (`JSON_TABLE(…) t` / `GRAPH_TABLE(…)
    // gt`) — the synthetic column names ARE the relation's projection.
    // Surface them directly (UPPER-folded per the Wave-1.5 bareword
    // convention below); subquery-body inference would find nothing (the
    // call args are not a SELECT). Mirrors mysqlResolver.ts /
    // pgsqlResolver.ts's syntheticColumns branch. Closes the Oracle
    // GRAPH_TABLE / JSON_TABLE downstream-columns gap (O2-T22 follow-up).
    if (binding.syntheticColumns && binding.syntheticColumns.length > 0) {
      pushOracleSubqueryColumns(out, binding.syntheticColumns, binding.alias);
      return;
    }
    if (binding.bodyRange && text.length > 0) {
      let inferred = inferSubqueryColumns({
        source: text,
        bodyRange: binding.bodyRange,
        engineId: 'oracle',
        snapshotTables: tables,
        cteBindings,
      });
      // Post-PIVOT shape: a `( … ) PIVOT (…) alias` reference resolves to the
      // pivoted columns — the FOR + aggregate-argument columns are consumed
      // and the value aliases are added. The pivot-interior caret is handled
      // separately (before this path runs), so reaching here means an OUTER
      // reference to the pivoted alias. Closes oracle script.52 §3.
      if (binding.pivotRange) {
        const parsed = parsePivotClause(
          text.slice(binding.pivotRange.start, binding.pivotRange.end),
        );
        if (parsed) inferred = applyPivotTransform(inferred, parsed);
      }
      pushOracleSubqueryColumns(out, inferred, binding.alias);
    }
    return;
  }
  if (binding.kind === 'cte') {
    const relationFolded = foldIdentifier(binding.relation);
    const cte = cteBindings.find((c) => foldIdentifier(c.name) === relationFolded);
    if (!cte) return;
    emitCteColumnList(
      out,
      cte,
      { source: text, engineId: 'oracle', snapshotTables: tables, cteBindings },
      binding.alias,
    );
    return;
  }
  // First try the direct snapshot-relation match.
  const relationFolded = foldIdentifier(binding.relation);
  const schemaFolded = binding.schema ? foldIdentifier(binding.schema) : null;
  const tbl = resolveRelation(tables, relationFolded, schemaFolded);
  if (tbl) {
    emitColumnsForTable(out, tbl, binding.alias);
    return;
  }
  // Fall through to the synonym chain — `FROM EMPS e` where `EMPS` is a
  // synonym for `HR.EMPLOYEES`. Resolves the target table from the cached
  // snapshot, then emits its columns under the alias.
  if (!schemaFolded) {
    const synMatch = findSynonymByName(synonyms, relationFolded);
    if (synMatch) {
      const targetTbl = resolveRelation(
        tables,
        synMatch.targetName.toUpperCase(),
        synMatch.targetSchema.toUpperCase(),
      );
      if (targetTbl) {
        emitColumnsForTable(out, targetTbl, binding.alias);
        return;
      }
    }
  }
  // Final fall-through — Oracle data-dictionary catalog view alias
  // (`FROM user_tables ut` → `ut.|` should emit `TABLE_NAME` etc.). These
  // views aren't part of the snapshot, so check the curated catalog map.
  const sysCols = ORACLE_SYSTEM_TABLE_COLUMNS[relationFolded];
  if (sysCols) {
    for (const name of sysCols) {
      out.push({
        label: name,
        insertText: formatIdentifier(name),
        isSnippet: false,
        kind: 'column',
        tier: 1,
        detail: `(${binding.alias})`,
      });
    }
  }
}

function emitColumnsForTable(
  out: CompletionCandidate[],
  tbl: SnapshotTable,
  aliasOrName: string,
): void {
  for (const c of tbl.columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${aliasOrName})`,
    });
  }
}

/**
 * Resolve a folded relation name to a snapshot table. When the binding
 * explicitly qualifies the schema, match `(schema, name)`. Otherwise match
 * any name (Oracle resolves unqualified names via the user's session schema
 * + public synonym chain — the snapshot tables are already filtered to the
 * connected schema). The exact spelling first: `ORDERS` and a quoted
 * `"orders"` are two tables, and the reference's fold names one of them.
 */
function resolveRelation(
  tables: ReadonlyArray<SnapshotTable>,
  relationFolded: string,
  schemaFolded: string | null,
): SnapshotTable | undefined {
  const inSchema = (t: SnapshotTable) =>
    !schemaFolded || t.schema === schemaFolded || (t.schema ?? '').toUpperCase() === schemaFolded;
  return (
    tables.find((t) => t.name === relationFolded && inSchema(t)) ??
    tables.find((t) => t.name.toUpperCase() === relationFolded && inSchema(t))
  );
}

/** The table a relation reference (`"Sales".orders` spelling) names, as Oracle resolves it. */
function oracleTableResolver(tables: ReadonlyArray<SnapshotTable>): RelationResolver<SnapshotTable> {
  return (name, schema) => resolveRelation(tables, foldIdentifier(name), schema ? foldIdentifier(schema) : null);
}

/** The table a bound relation names, as Oracle resolves it — FK / loose-FK JOIN identity. */
function oracleBindingResolver(tables: ReadonlyArray<SnapshotTable>): (binding: AliasBinding) => SnapshotTable | undefined {
  const resolve = oracleTableResolver(tables);
  return (b) => resolve(b.relation, b.schema || undefined);
}

function emitSynonymTargetColumns(
  out: CompletionCandidate[],
  syn: OracleSchemaSynonym,
  tables: ReadonlyArray<SnapshotTable>,
): void {
  const targetTbl = resolveRelation(
    tables,
    syn.targetName.toUpperCase(),
    syn.targetSchema.toUpperCase(),
  );
  if (!targetTbl) return;
  emitColumnsForTable(out, targetTbl, syn.name);
}

function findSynonymByName(
  synonyms: readonly OracleSchemaSynonym[],
  baseFolded: string,
): OracleSchemaSynonym | undefined {
  // Three-step chain: prefer a private synonym (isPublic=false) over a
  // public one when both exist with the same name. Iterate twice to keep
  // the rule explicit.
  const priv = synonyms.find(
    (s) => !s.isPublic && s.name.toUpperCase() === baseFolded,
  );
  if (priv) return priv;
  return synonyms.find((s) => s.isPublic && s.name.toUpperCase() === baseFolded);
}

type CteResolveArgs = {
  readonly source: string;
  readonly engineId: DatabaseEngineId;
  readonly snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly cteBindings: readonly CteBinding[];
};

function emitCteColumnList(
  out: CompletionCandidate[],
  cte: CteBinding,
  resolveArgs: CteResolveArgs,
  aliasOrCteName?: string,
): void {
  const cols = resolveCteColumns({ cte, ...resolveArgs });
  if (cols.length === 0) return;
  const detailContext = aliasOrCteName ?? cte.name;
  for (const col of cols) {
    out.push({
      label: col,
      insertText: col,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `Column (${detailContext})`,
    });
  }
}

/**
 * Cheap source-text walk. Detects whether the cursor sits immediately after
 * an Oracle bind-variable (`:bind`) or SQL*Plus substitution (`&sub` /
 * `&&sub`) sigil, optionally with an in-progress identifier suffix. When
 * true, the resolver returns no suggestions — these positions are not
 * identifier completion targets.
 *
 * Walks back through any in-progress bare-identifier characters then
 * inspects the leading char(s):
 *   - `:` → bind (single-character sigil)
 *   - `&&` or `&` → substitution
 */
function isCursorAfterBindOrSubstitution(source: string, offset: number): boolean {
  // Walk back through in-progress identifier characters.
  let i = offset;
  while (i > 0 && isIdentChar(source[i - 1]!)) i--;
  if (i === 0) return false;
  const prev = source[i - 1];
  if (prev === ':') return true;
  if (prev === '&') return true;
  // Defensive: `&&` parses as two `&` so the loop above stops at the
  // second `&`. The single-char check above already returns true for the
  // first one we encounter, so no extra logic is needed.
  return false;
}

function isIdentChar(ch: string): boolean {
  return (
    (ch >= 'A' && ch <= 'Z') ||
    (ch >= 'a' && ch <= 'z') ||
    (ch >= '0' && ch <= '9') ||
    ch === '_' ||
    ch === '$' ||
    ch === '#'
  );
}

// Oracle bareword shape: `[A-Za-z][A-Za-z0-9_$#]*`. Identifiers matching this
// pattern fold to UPPERCASE under Oracle's data-dictionary convention when
// they appear unquoted in DDL. Names that fail the test almost always
// originated as `"Quoted bodies with spaces"` and Oracle preserves their
// case verbatim — we mirror that policy here. See `oracleIdentifiers.ts`
// for the canonical helper documentation.
const ORACLE_BAREWORD_RE = /^[A-Za-z][A-Za-z0-9_$#]*$/;

/**
 * Apply Oracle's UPPER-fold convention to a column / alias name returned by
 * `inferSubqueryColumns`. The helper sits in the resolver because the upstream
 * `parseIdentifierToken` already strips outer quotes — by the time the name
 * reaches us we cannot distinguish a bare `employee_id` from a quoted
 * `"employee_id"`. The pragmatic choice (mirroring how Oracle DDL is written
 * in practice): treat any bareword-shaped name as a target for UPPER-folding,
 * preserve everything else verbatim. The rare quoted-bareword case is a
 * known limitation noted in the Oracle audit; the common case (lowercase
 * source-text columns appearing in a sub-select) now matches the dictionary.
 */
function canonicalizeInferredOracleName(name: string): string {
  if (name.length === 0) return name;
  if (!ORACLE_BAREWORD_RE.test(name)) return name;
  return name.toUpperCase();
}

// Test-only exports so unit tests can exercise identifier helpers without
// standing up the full Monaco translation.
export function __testNeedsDoubleQuote(s: string): boolean {
  return needsDoubleQuote(s);
}

export function __testDoubleQuote(s: string): string {
  return doubleQuote(s);
}

export function __testUnquote(s: string): string {
  return unquoteIdentifier(s);
}

export function __testFold(s: string): string {
  return foldIdentifier(s);
}

export function __testEqIdentifier(a: string, b: string): boolean {
  return eqIdentifier(a, b);
}

export function __testFormatQualifiedTwoPart(schema: string, name: string): string {
  return formatQualifiedTwoPart(schema, name);
}

export function __testIsCursorAfterBindOrSubstitution(text: string, offset: number): boolean {
  return isCursorAfterBindOrSubstitution(text, offset);
}

export function __testCanonicalizeInferredOracleName(name: string): string {
  return canonicalizeInferredOracleName(name);
}


/**
 * Workstream C oracle 18.§1 — the column set treated as JSON at a
 * `JSON_VALUE(p.▮` slot: buffer-affinity (columns the buffer already uses
 * as JSON_* first args) ∪ the binding's bus-classified columns ∪ its
 * MetaInfo-declared ("Treat as JSON") columns. Lower-cased — the affinity
 * module compares case-insensitively.
 */
function collectKnownJsonColumns(
  args: BuildArgs,
  binding: AliasBinding,
  tables: ReadonlyArray<SnapshotTable>,
): Set<string> {
  const cols = collectBufferJsonFunctionColumns(args.text);
  const relFolded = foldIdentifier(binding.relation);
  const tbl = tables.find(
    (t) =>
      foldIdentifier(t.name) === relFolded &&
      (!binding.schema || foldIdentifier(t.schema ?? '') === foldIdentifier(binding.schema)),
  );
  if (!tbl) return cols;
  const schema = tbl.schema ?? '';
  for (const c of tbl.columns) {
    const busJson = !!c.jsonClassification && c.jsonClassification.kind !== 'not-json';
    const declared =
      !busJson &&
      !!args.connectionId &&
      !!args.database &&
      isJsonColumnDeclared(args.connectionId, args.database, schema, tbl.name, c.name);
    if (busJson || declared) cols.add(c.name.toLowerCase());
  }
  return cols;
}
